/* ============================================================
   PlusPet theme.js
   - Drawers accesibles (carrito + navegación) con focus trap
   - Carrito vía /cart.js (+/- , eliminar) y persistencia de entrega
   - Selector de variantes en la ficha de producto
   - Galería de producto accesible por teclado
   ============================================================ */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // Keep responsive candidates in sync when a shopper changes the variant.
  function updateProductImage(img, src, originalWidth) {
    if (!img || !src || img.getAttribute('src') === src) return;
    img.setAttribute('fetchpriority', 'high');
    const maxWidth = Number(img.dataset.responsiveWidth);
    if (maxWidth) {
      const limit = Math.min(maxWidth, Number(originalWidth) || maxWidth);
      const widths = [160, 192, 200, 240, 280, 320, 360, 400, 480, 600, 900].filter(width => width < limit);
      widths.push(limit);
      img.srcset = widths.map(width => {
        const url = new URL(src, document.baseURI);
        url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      }).join(', ');
    } else {
      img.removeAttribute('srcset');
    }
    img.src = src;
    // La foto ampliada (Lovable (7)) sigue a la de la variante.
    if (img.dataset.zoomSrc) {
      const zoom = new URL(src, document.baseURI);
      zoom.searchParams.set('width', Math.min(2048, Number(originalWidth) || 2048));
      img.dataset.zoomSrc = zoom.href;
    }
  }

  /* ============================================================
     Drawer reutilizable con gestión de foco (focus trap)
     ============================================================ */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function createDrawer(drawer, overlay, opts) {
    opts = opts || {};
    if (!drawer) return null;

    let lastFocused = null;
    // Regiones de fondo (hermanas del drawer); nunca el body ni un ancestro del drawer.
    const bgRegions = $$('#MainContent, .ppt-header, .ppt-footer, .ppt-bottomnav, header, footer');

    const getFocusable = () => $$(FOCUSABLE, drawer).filter((el) => el.offsetParent !== null);

    function setBackgroundInert(on) {
      bgRegions.forEach((el) => {
        if (el === drawer || drawer.contains(el) || el.contains(drawer) || (opts.keepInteractive && el.matches(opts.keepInteractive))) return;
        if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
        else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
      });
    }

    const isOpen = () => drawer.classList.contains('is-open');

    function open(e) {
      if (e) e.preventDefault();
      if (!isOpen() && !drawer.contains(document.activeElement)) lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.removeAttribute('inert');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('drawer-open');
      setBackgroundInert(true);
      if (typeof opts.onOpen === 'function') opts.onOpen();
      const closeBtn = opts.closeSelector ? $(opts.closeSelector, drawer) : null;
      (closeBtn || drawer).focus();
    }

    function close() {
      if (!isOpen()) return;
      // Inert alone does not consistently dismiss the mobile keyboard.
      if (drawer.contains(document.activeElement)) document.activeElement.blur();
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('inert', '');
      drawer.removeAttribute('aria-modal');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('drawer-open');
      setBackgroundInert(false);
      if (lastFocused?.isConnected && !drawer.contains(lastFocused) && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
      lastFocused = null;
      if (opts.trigger) opts.trigger.setAttribute('aria-expanded', 'false');
    }

    // Estado inicial cerrado: quitar los descendientes del orden de foco.
    if (!isOpen()) drawer.setAttribute('inert', '');

    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (!isOpen()) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        const f = getFocusable();
        if (f.length === 0) { e.preventDefault(); drawer.focus(); return; }
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    return { open, close, isOpen };
  }

  /* ============================================================
     Escape de HTML para interpolaciones en innerHTML
     ============================================================ */
  const escapeHtml = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  // Limpia el título para mostrarlo: saca el prefijo genérico "Alimento" y la
  // marca, esté donde esté. Espeja el filtro Liquid de snippets/product-card.
  function cleanTitle(title, vendor, on) {
    let t = String(title || '');
    if (!on) return t;
    if (t.indexOf('Alimento ') === 0) t = t.slice(9);
    if (vendor) {
      // Rodeada de espacios = palabra completa. El "+" pegado es parte de la
      // MARCA (la línea Agility+ tiene vendor "Agility" a secas).
      let padded = ' ' + t + ' ';
      const plus = ' ' + vendor + '+ ';
      const plain = ' ' + vendor + ' ';
      if (padded.indexOf(plus) !== -1) padded = padded.replace(plus, ' ');
      else if (padded.indexOf(plain) !== -1) padded = padded.replace(plain, ' ');
      const limpio = padded.trim();
      if (limpio) t = limpio; // si el título era sólo la marca, se deja como estaba
    }
    return t.trim();
  }

  // Lovable (CartItemsList): cada producto del carrito dice marca + nombre.
  // El título limpio saca la marca (en las cards va arriba como etiqueta); acá
  // no hay etiqueta, así que vuelve adelante.
  function cartLineTitle(it, on) {
    const t = cleanTitle(it.product_title, it.vendor, on);
    if (!on || !it.vendor) return t;
    return t.indexOf(it.vendor) === 0 ? t : it.vendor + ' ' + t;
  }

  /* ============================================================
     Avisos sobre el CTA (Lovable lib/cartToast). Lovable (6), 03/10: fondo
     negro (snackbar-dark), sin ícono y con una X de 44px. "Producto
     agregado" se va a los 2 s; "ya agregaste todo el stock" suma la acción
     «Ver carrito» y dura lo que el default de sonner (4 s). Viven fuera del
     carrito, así que tocarlos no lo cierra. Uno por vez.
     ============================================================ */
  const TOAST_MS = 4000;
  const ADDED_TOAST_MS = 2000;
  function showToast(text, { duration = TOAST_MS, action = null } = {}) {
    const toaster = document.querySelector('[data-toaster]');
    if (!toaster) return null;
    const toast = document.createElement('div');
    toast.className = 'ppt-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = '<span class="ppt-toast__text"></span>' +
      (action ? '<button type="button" class="ppt-toast__action"></button>' : '') +
      '<button type="button" class="ppt-toast__close" aria-label="Cerrar aviso"><span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></span></button>';
    toast.querySelector('.ppt-toast__text').textContent = text;
    toaster.querySelectorAll('.ppt-toast').forEach((t) => t.remove());
    toaster.appendChild(toast);
    let timer = setTimeout(dismiss, duration);
    function dismiss() {
      clearTimeout(timer);
      if (!toast.isConnected) return;
      toast.classList.add('is-leaving');
      setTimeout(() => toast.remove(), 200);
    }
    toast.querySelector('.ppt-toast__close').addEventListener('click', (e) => { e.stopPropagation(); dismiss(); });
    if (action) {
      const btn = toast.querySelector('.ppt-toast__action');
      btn.textContent = action.label;
      btn.addEventListener('click', (e) => { e.stopPropagation(); dismiss(); action.onClick(); });
    }
    // Como sonner: con el puntero encima no se va.
    toast.addEventListener('pointerenter', () => clearTimeout(timer));
    toast.addEventListener('pointerleave', () => { clearTimeout(timer); timer = setTimeout(dismiss, duration); });
    return toast;
  }
  function notifyProductAdded(brand, name) {
    return showToast([brand, name].filter(Boolean).join(' ') + ' agregado al carrito', { duration: ADDED_TOAST_MS });
  }
  // Lovable (6): «Agregar» en la ficha con todo el stock ya en el carrito.
  function notifyMaxStockReached() {
    return showToast('Ya agregaste todas las unidades disponibles al carrito.', {
      action: { label: 'Ver carrito', onClick: () => cartDrawer && cartDrawer.open() },
    });
  }
  window.__pptNotifyMaxStockReached = notifyMaxStockReached;
  window.__pptNotifyProductAdded = notifyProductAdded;

  /* ============================================================
     Formato de moneda — usa el money_format de la tienda (tokens de Shopify)
     en vez de hardcodear símbolo y locale.
     ============================================================ */
  const MONEY_FORMAT =
    (document.querySelector('[data-money-format]') || {}).dataset?.moneyFormat || '${{amount}}';

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    const value = (cents / 100).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    const centsPart = parts[1] ? decimal + parts[1] : '';
    return dollars + centsPart;
  }

  // Espeja el filtro Liquid `money_without_trailing_zeros`: los precios redondos
  // se muestran sin `,00` (planilla: "todos los precios se muestran con
  // decimales"), pero un importe con centavos reales los conserva.
  function decimalsFor(cents, precision) {
    if (precision === 0) return 0;
    return Math.round(Math.abs(cents)) % 100 === 0 ? 0 : precision;
  }

  function money(cents) {
    const fmt = MONEY_FORMAT;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholder);
    const fmt2 = (c, p, t, d) => formatWithDelimiters(c, decimalsFor(c, p), t, d);
    let value = '';
    switch (match && match[1]) {
      case 'amount':
        value = fmt2(cents, 2, '.', ','); break;
      case 'amount_no_decimals':
        value = fmt2(cents, 0, '.', ','); break;
      case 'amount_with_comma_separator':
        value = fmt2(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = fmt2(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = fmt2(cents, 2, "'", ','); break;
      case 'amount_with_space_separator':
        value = fmt2(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = fmt2(cents, 0, ' ', ','); break;
      default:
        value = fmt2(cents, 2, '.', ',');
    }
    return fmt.replace(placeholder, value);
  }

  // Importe sin decimales (paridad lovable: maximumFractionDigits: 0). Se usa en
  // la barra de incentivo, donde el chip del marcador tiene que quedar angosto.
  function moneyRound(cents) {
    const fmt = MONEY_FORMAT;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholder);
    const name = (match && match[1]) || '';
    let delim = '.';
    if (name.indexOf('apostrophe') !== -1) delim = "'";
    else if (name.indexOf('space') !== -1) delim = ' ';
    return fmt.replace(placeholder, formatWithDelimiters(cents, 0, delim, ','));
  }

  // Expuesto para los tests, igual que __pptUpdateIncentive: el formato de plata
  // depende del money_format de la tienda y no hay forma de ejercitarlo sin
  // levantar todo el carrito.
  window.__pptMoney = money;

  /* ============================================================
     Carrito (drawer)
     ============================================================ */
  const cartDrawerEl = $('[data-cart-drawer]');
  const cartOverlayEl = $('[data-cart-overlay]');
  const cartDrawer = createDrawer(cartDrawerEl, cartOverlayEl, {
    closeSelector: '[data-cart-close]',
    onOpen: () => { setCartView('items'); if (cartState) renderCart(cartState); if (!_cartAddPending) refreshCart(); },
  });

  if (cartDrawer) {
    // El ícono de carrito abre el drawer (no navega a /cart).
    $$('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); cartDrawer.open(); }));
    $$('[data-cart-close]').forEach((b) => b.addEventListener('click', cartDrawer.close));
    // Si venís de /cart (redirigido al home), abrir el drawer con el flujo de
    // entrega. Deferimos con setTimeout(0) para correr después de que el IIFE
    // inicialice cartState/refreshCart (declarados más abajo); luego traemos
    // /cart.js, renderizamos y recién ahí abrimos (si abrimos antes, no hay items).
    try {
      if (sessionStorage.getItem('ppt-open-cart')) {
        sessionStorage.removeItem('ppt-open-cart');
        setTimeout(function () { refreshCart().then(function () { cartDrawer.open(); }); }, 0);
      }
    } catch (e) {}
  }

  function showCartError(text) {
    const el = $('[data-cart-error]');
    if (!el) return;
    el.textContent = text;
    el.hidden = $('[data-cart-drawer]')?.dataset.cartCurrentView === 'delivery';
  }
  function hideCartError() {
    const el = $('[data-cart-error]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  async function changeLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => null);
        showCartError((e && (e.description || e.message)) || 'No se pudo actualizar la cantidad.');
      } else {
        hideCartError();
      }
      refreshCart(); // resincroniza la cantidad mostrada haya o no error
    } catch (err) {
      console.error('[PlusPet] change line', err);
    }
  }

  // Cambio de vista del drawer (items <-> delivery). El estado vacío manda:
  // si el carrito está vacío se ocultan vistas y footers y solo se ve el empty state.
  function setCartView(view) {
    const dr = $('[data-cart-drawer]');
    if (!dr) return;
    dr.setAttribute('data-cart-current-view', view);
    const isEmpty = dr.getAttribute('data-cart-empty-state') === '1';
    const isDelivery = view === 'delivery';
    const cartError = $('[data-cart-error]', dr);
    if (cartError) cartError.hidden = isDelivery || !cartError.textContent;
    $$('[data-cart-view]', dr).forEach((el) => { el.hidden = isEmpty || el.getAttribute('data-cart-view') !== view; });
    $$('[data-cart-footer]', dr).forEach((el) => { el.hidden = isEmpty || el.getAttribute('data-cart-footer') !== view; });
    $$('[data-cart-continue]', dr).forEach(el => { el.disabled = _cartAddPending; });
    const back = $('[data-cart-back]', dr);
    if (back) back.hidden = isEmpty || !isDelivery;
    const title = $('[data-cart-title]', dr);
    if (title && title.firstChild) title.firstChild.textContent = isEmpty ? 'Tu carrito ' : (isDelivery ? 'Entrega y horario ' : 'Tu carrito ');
    dr.dispatchEvent(new CustomEvent('ppt:cart-view', { detail: view, bubbles: true }));
  }
  (function bindCartViewNav() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-continue]')) { setCartView('delivery'); }
      else if (e.target.closest('[data-cart-back]')) { setCartView('items'); }
    });
  })();

  // "Resumen del pedido" (Lovable 28/09, CartTotals): en Tu carrito arranca
  // abierto si el drawer tiene alto de sobra (650px) y en Entrega y horario
  // arranca cerrado. Si el comprador lo abre o cierra, se respeta. En la
  // entrega se adapta al espacio antes de interactuar; la elección explícita
  // del comprador tiene prioridad sobre los cambios de teclado y layout.
  (function initCartSummary() {
    const dr = $('[data-cart-drawer]');
    if (!dr) return;
    const touched = { items: false, delivery: false };
    const summary = (view) => $('[data-cart-summary][data-summary-view="' + view + '"]', dr);
    const currentView = () => dr.getAttribute('data-cart-current-view') || 'items';
    let activeView = currentView();
    let programmatic = false;
    function setOpen(el, open) {
      if (!el || el.open === open) return;
      programmatic = true;
      el.open = open;
      // El evento toggle llega asincrónico: se marca acá y se limpia ahí.
    }
    function crowded(el) {
      const footer = el.closest('[data-cart-footer]');
      if (!footer || footer.hidden) return false;
      const top = footer.getBoundingClientRect().top;
      return $$('[data-step="day"].is-active [data-day-grid], [data-step="day"].is-active [data-time-grid]', dr).some((f) => {
        if (f.hidden || f.closest('[hidden]')) return false;
        const r = f.getBoundingClientRect();
        return r.top < top && r.bottom > top - 8;
      });
    }
    function adapt() {
      const view = currentView();
      const el = summary(view);
      if (!el) return;
      // An explicit open/close or discount interaction wins over automatic
      // height/collision adjustments, including while a result is displayed.
      if (touched[view]) return;
      if (el.open && crowded(el)) { setOpen(el, false); return; }
      if (view === 'delivery') setOpen(el, false);
      else setOpen(el, dr.getBoundingClientRect().height >= 650);
    }
    // Como en Lovable (el pie se vuelve a montar al cambiar de vista), entrar a
    // una vista arranca de nuevo con su estado inicial.
    dr.addEventListener('ppt:cart-view', (e) => {
      // renderCart also emits this event when it only refreshes totals. That
      // isn't navigation: preserve the shopper's summary and discount state.
      if (e.detail !== activeView) {
        activeView = e.detail;
        touched[activeView] = false;
      }
      requestAnimationFrame(adapt);
    });
    dr.addEventListener('toggle', (e) => {
      const el = e.target;
      if (!el.matches || !el.matches('[data-cart-summary]')) return;
      if (programmatic) { programmatic = false; return; }
      touched[el.dataset.summaryView] = true;
      if (el.open) requestAnimationFrame(adapt);
    }, true);
    // Al terminar día y horario se abre el resumen de la entrega (si estaba
    // cerrado): lo que sigue es mirar el total e "Ir a pagar". Cuenta como
    // abierto a propósito, así el ajuste automático no lo vuelve a cerrar.
    dr.addEventListener('ppt:delivery-complete', () => {
      const el = summary('delivery');
      if (!el || el.open) return;
      touched.delivery = true;
      setOpen(el, true);
    });
    // CART 54: usar el resumen (escribir un código, tocar "Ingresar código")
    // cuenta como tocarlo. Si no, al abrirse el teclado del celular el
    // carrito se achicaba de 650px, el resumen se cerraba solo y no se veía
    // si el código se aplicó o dio error.
    function rememberInteraction(e) {
      const el = e.target.closest && e.target.closest('[data-cart-summary]');
      if (!el) return;
      const view = el.dataset.summaryView;
      touched[view] = true;
    }
    dr.addEventListener('focusin', rememberInteraction);
    // Touch browsers need not focus a button. Remember the action before its
    // handler disables/hides the control or asynchronously refreshes the cart.
    dr.addEventListener('click', rememberInteraction, true);
    $('[data-cart-body]', dr)?.addEventListener('scroll', adapt, { passive: true });
    window.addEventListener('resize', adapt);
    requestAnimationFrame(adapt);
  })();

  // Estado local del carrito para actualizaciones optimistas (UI instantánea, sync en background).
  let cartState = null;
  // CART 44: el carrito de Liquid (el inicial y el de la sección cart-state)
  // no trae discount_codes; los códigos que aplican vienen aparte.
  function withDiscountCodes(cart, root = document, fallback = []) {
    if (!cart || Array.isArray(cart.discount_codes)) return cart;
    const el = $('[data-cart-discount-codes]', root);
    try { cart.discount_codes = el ? JSON.parse(el.textContent).map(code => ({ code, applicable: true })) : fallback; }
    catch (_) { cart.discount_codes = fallback; }
    return cart;
  }
  try { cartState = withDiscountCodes(JSON.parse($('[data-cart-initial]')?.textContent || 'null')); } catch (_) {}

  let _cartAddPending = false;

  // Delivery, discounts and empty-cart cleanup share one write queue. A late
  // delivery/discount write must never restore the previous order's options.
  let cartUpdateQueue = Promise.resolve();
  function updateCart(body, opts) {
    const write = async () => {
      const res = await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body), keepalive: !!opts?.keepalive,
      });
      if (!res.ok) throw new Error('cart/update.js ' + res.status);
      return res;
    };
    const pending = cartUpdateQueue.then(write, write);
    cartUpdateQueue = pending.catch(() => {});
    return pending;
  }
  let cartHadItems = cartState?.item_count > 0;
  let emptyCartReset = null;
  let emptyCartToReset = null;
  let promoRun = 0;
  // Una sola lista (delivery-logic.js): a mano quedaban afuera claves nuevas,
  // como Pickup-Location-*, y el carrito no las vaciaba.
  function zapietAttributeKeys() {
    return (window.PlusPetDelivery && window.PlusPetDelivery.ZAPIET_ATTRIBUTE_KEYS) || [
      'Checkout-Method', 'Delivery-Date', 'Delivery-Time', 'Pickup-Date', 'Pickup-Time',
      'Pickup-Location-Company', 'Pickup-Location-Address-Line-1', 'Pickup-Location-City',
      'Pickup-Location-Region', 'Pickup-Location-Postal-Code', 'Pickup-Location-Country',
    ];
  }
  const deliveryAttributeKeys = [
    '_pluspet_method', '_pluspet_shop_domain', '_pluspet_zone_id', '_pluspet_window_id',
    '_pluspet_slot_date', '_pluspet_slot_start', '_pluspet_slot_end',
    '_pluspet_cart_token', '_pluspet_address_postal_code',
    // Atributos de Zapiet que escribe cartAttributes (mails y etiquetas).
    ...zapietAttributeKeys(),
  ];
  const hasCartOptions = cart => !!(cart?.note || cart?.discount_codes?.length ||
    Object.entries(cart?.attributes || {}).some(([key, value]) => key.startsWith('_pluspet_') && value));

  async function confirmCart(cart) {
    if (cart.item_count !== 0) {
      cartHadItems = cart.item_count > 0;
      if (!emptyCartReset) emptyCartToReset = null;
      return cart;
    }
    if (!cartHadItems && !hasCartOptions(cart) && !emptyCartToReset) return cart;
    emptyCartToReset = cart;
    if (!emptyCartReset) {
      const attributes = Object.fromEntries([...new Set([...deliveryAttributeKeys,
        ...Object.keys(cart.attributes || {}).filter(key => key.startsWith('_pluspet_'))])].map(key => [key, '']));
      // Only an authoritative empty response gets here, never an optimistic
      // removal. Invalidate in-flight UI work and clear both copies of the form.
      promoRun++;
      if (cartDrawerEl?.contains(document.activeElement) && document.activeElement.matches('input, textarea')) {
        document.activeElement.blur();
      }
      $$('[data-promo-input]').forEach(el => { el.value = ''; });
      $$('[data-promo-apply]').forEach(el => { el.disabled = true; });
      $$('[data-promo-applied]').forEach(el => { el.hidden = true; });
      promoError(''); announcePromo(''); showPromoField(false);
      cartDrawerEl?.dispatchEvent(new CustomEvent('ppt:cart-emptied', { detail: cart }));
      emptyCartReset = updateCart({ attributes, discount: '', note: '' }).then(() => {
        cartHadItems = false; emptyCartToReset = null;
      }).finally(() => { emptyCartReset = null; });
    }
    await emptyCartReset;
    Object.keys(cart.attributes || {}).filter(key => key.startsWith('_pluspet_')).forEach(key => { delete cart.attributes[key]; });
    cart.discount_codes = []; cart.total_discount = 0; cart.note = '';
    return cart;
  }

  // Shopify cart.js omits inventory. Liquid supplies real variant limits;
  // null means untracked inventory or an explicit continue-selling policy.
  const variantStock = new Map();
  function rememberStock(variants) {
    variants.forEach(v => {
      if (v.max_stock === null) variantStock.set(String(v.id), Infinity);
      else if (Number.isFinite(v.max_stock)) variantStock.set(String(v.id), Math.max(0, v.max_stock));
    });
  }
  $$('[data-cart-product]').forEach(el => {
    try { rememberStock(JSON.parse(el.textContent).variants || []); } catch (_) {}
  });
  function readCartStock(root = document) {
    root.querySelectorAll('[data-cart-stock]').forEach(el => {
      try { rememberStock(JSON.parse(el.textContent)); } catch (_) {}
    });
  }
  readCartStock();
  const stockLimit = id => variantStock.get(String(id)) ?? Infinity;
  function cartVariantQuantity(id, exceptKey) {
    return (cartState?.items || []).reduce((sum, item) =>
      String(item.variant_id || item.id) === String(id) && item.key !== exceptKey ? sum + item.quantity : sum, 0);
  }
  function availableStock(id, exceptKey) {
    return Math.max(0, stockLimit(id) - cartVariantQuantity(id, exceptKey));
  }
  // Mismos cortes que el Liquid de la card y de main-product: con 10 o más no
  // se muestra el número exacto.
  function stockCopy(left) {
    if (left <= 0) return { text: 'Sin stock', level: 'out' };
    if (left >= 10) return { text: '+10 unidades', level: 'ok' };
    if (left === 1) return { text: '1 unidad', level: 'critical' };
    return { text: left + ' unidades', level: 'low' };
  }
  function showProductStock(form, inventory) {
    const copy = stockCopy(inventory);
    const card = form.closest('[data-ppt-card]');
    const cardStock = card && card.querySelector('[data-card-stock]');
    if (cardStock) {
      const txtEl = cardStock.querySelector('[data-card-stock-text]');
      if (txtEl) txtEl.textContent = copy.text;
      cardStock.hidden = false;
      cardStock.setAttribute('data-stock-level', copy.level);
      cardStock.classList.toggle('ppt-card__stock--out', copy.level === 'out');
      return;
    }
    const pdpStock = form.querySelector('#PdpAddBtn') ? $('#PdpStock') : null;
    if (pdpStock) {
      const city = pdpStock.dataset.city || 'Mendoza';
      const strong = copy.level === 'critical' ? 'Última unidad' : copy.text;
      const html = withStockGift(copy.level === 'out' ? 'Sin stock en ' + city : '<strong>' + strong + '</strong> en ' + city, pdpStock);
      const txtEl = $('#PdpStockTxt');
      if (txtEl) txtEl.innerHTML = html; else pdpStock.innerHTML = html;
      pdpStock.classList.toggle('ppt-pdp__stock--out', copy.level === 'out');
      pdpStock.setAttribute('data-stock-level', copy.level);
      pdpStock.style.display = '';
    }
  }
  // "+10 unidades con 3kg de regalo en Mendoza": los kilos van pegados al
  // fragmento fuerte, sin su color (Lovable (4), StockLine).
  function withStockGift(html, stockEl) {
    const kg = stockEl && stockEl.dataset.giftKg;
    if (!kg || html.indexOf('</strong> en ') === -1) return html;
    return html.replace('</strong> en ', '</strong><b class="ppt-pdp__stockgift"> con ' + kg + 'kg de regalo</b> en ');
  }
  function syncProductStock(onlyForm) {
    const forms = onlyForm ? [onlyForm] : $$('form[action="/cart/add"], form[action$="/cart/add"]');
    forms.forEach(form => {
      try {
        const product = JSON.parse($('[data-cart-product]', form)?.textContent || 'null');
        const id = form.querySelector('[name="id"]')?.value;
        const variant = product?.variants.find(v => String(v.id) === String(id));
        if (!variant || !Object.prototype.hasOwnProperty.call(variant, 'max_stock')) return;
        if (!variantStock.has(String(id))) rememberStock([variant]);
        const inventory = stockLimit(id);
        const left = availableStock(id);
        const canAdd = variant.available !== false && left > 0;
        const quantity = form.querySelector('[name="quantity"]');
        if (quantity) {
          if (Number.isFinite(left)) {
            quantity.max = String(Math.max(1, left));
            if (Number(quantity.value) > left) quantity.value = String(Math.max(1, left));
          } else quantity.removeAttribute('max');
          quantity.dispatchEvent(new Event('ppt:max'));
        }
        // Agregar al carrito no reserva inventario: mostrar el stock de tienda.
        // Sólo el tope de cantidad y el bloqueo descuentan lo que ya está en carrito.
        if (Number.isFinite(inventory) && variant.available !== false) showProductStock(form, inventory);
        const button = form.querySelector('[type="submit"]');
        if (button && button.getAttribute('aria-busy') !== 'true') {
          const text = button.querySelector('#PdpAddTxt');
          // Lovable (6): con todo el stock ya en el carrito, el botón sigue
          // habilitado y avisa en vez de agregar. Lovable lo hace sólo en la
          // ficha; el usuario (04/10) lo pidió igual en todos lados, así que
          // vale también para las tarjetas. Sin stock real queda "Agotado".
          const maxReached = variant.available !== false && inventory > 0 && !canAdd;
          button.disabled = !canAdd && !maxReached;
          if (maxReached) button.dataset.maxReached = '1'; else delete button.dataset.maxReached;
          const label = variant.available !== false && inventory > 0 ? 'Agregar' : 'Agotado';
          if (text) text.textContent = label;
          else if (button.hasAttribute('data-card-add')) button.textContent = label;
        }
      } catch (_) {}
    });
  }
  syncProductStock();

  let cartItemOrder = [];
  try { cartItemOrder = JSON.parse(sessionStorage.getItem('ppt_cart_item_order') || '[]'); } catch (_) {}
  if (!Array.isArray(cartItemOrder)) cartItemOrder = [];
  function keepCartOrder(cart) {
    const identity = (item) => JSON.stringify([
      String(item.variant_id || item.id || item.key),
      item.selling_plan_allocation?.selling_plan?.id || null,
      Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    ]);
    const present = cart.items.map(identity);
    cartItemOrder = cartItemOrder.filter(key => present.includes(key));
    present.forEach(key => { if (!cartItemOrder.includes(key)) cartItemOrder.push(key); });
    cart.items.sort((a, b) => cartItemOrder.indexOf(identity(a)) - cartItemOrder.indexOf(identity(b)));
    try { sessionStorage.setItem('ppt_cart_item_order', JSON.stringify(cartItemOrder)); } catch (_) {}
  }

  function recomputeCartTotals(cart) {
    cart.items.forEach((it) => {
      it.final_line_price = (it.final_price != null ? it.final_price : it.price) * it.quantity;
      it.original_line_price = (it.original_price != null ? it.original_price : it.price) * it.quantity;
    });
    cart.item_count = cart.items.reduce((n, it) => n + it.quantity, 0);
    cart.total_price = cart.items.reduce((s, it) => s + it.final_line_price, 0);
    cart.original_total_price = cart.items.reduce((s, it) => s + it.original_line_price, 0);
  }

  // Cada cambio optimista (+/−/quitar) incrementa este contador. Toda respuesta
  // del servidor que se pidió ANTES del último cambio local es vieja para la UI:
  // si la pintáramos, la cantidad y la barra de incentivo retrocederían un
  // instante hasta que responda el change.js del cambio nuevo (se veía como
  // "sube → baja → sube" al tocar + apenas abierto el drawer, porque onOpen
  // dispara refreshCart y /cart.js llega después del click). El cambio nuevo
  // tiene su propio change.js pendiente, que trae la verdad final.
  let _cartMutSeq = 0;
  const cartResponseIsStale = (mutSeq) => mutSeq !== _cartMutSeq;

  let _refreshSeq = 0;
  async function refreshCart(allowPending = false) {
    const seq = ++_refreshSeq;
    const mut = _cartMutSeq;
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('No se pudo consultar el carrito.');
      const cart = await res.json();
      if (_cartAddPending && !allowPending) return;
      if (seq !== _refreshSeq) return; // respuesta obsoleta: llegó un refresh más nuevo
      if (cartResponseIsStale(mut)) return; // hubo un cambio local mientras volaba
      await confirmCart(cart);
      if (seq !== _refreshSeq || cartResponseIsStale(mut)) return;
      cartState = cart;
      renderCart(cart);
      return cart;
    } catch (err) {
      console.error('[PlusPet] cart refresh', err);
    }
  }

  // +/- y eliminar optimistas: muta el estado local, re-render inmediato y sincroniza
  // con /cart/change.js en background (debounce por línea). Reconcilia con la respuesta
  // sólo si no hubo otro cambio local después de mandarla.
  const _cartChangeTimers = {};
  const _cartChangeRequests = new Set();
  function optimisticChange(key, quantity) {
    if (_cartAddPending) return;
    if (!cartState || !cartState.items) { changeLine(key, quantity); return; }
    const line = cartState.items.find((i) => i.key === key);
    if (!line) { changeLine(key, quantity); return; }
    if (quantity > line.quantity && quantity > availableStock(line.variant_id || line.id, key)) return;
    _cartMutSeq++;
    if (quantity <= 0) cartState.items = cartState.items.filter((i) => i.key !== key);
    else line.quantity = quantity;
    recomputeCartTotals(cartState);
    renderCart(cartState);
    clearTimeout(_cartChangeTimers[key]);
    _cartChangeTimers[key] = setTimeout(() => {
      delete _cartChangeTimers[key];
      const mut = _cartMutSeq;
      const request = fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) })
        .then((r) => (r.ok ? r.json() : r.json().then((er) => Promise.reject(er))))
        .then(async (cart) => {
          hideCartError();
          if (cartResponseIsStale(mut)) return; // ya hay otro cambio local con su propio change.js
          await confirmCart(cart);
          if (cartResponseIsStale(mut)) return;
          cartState = cart;
          renderCart(cart);
        })
        .catch((er) => { showCartError((er && (er.description || er.message)) || 'No se pudo actualizar la cantidad.'); return refreshCart(); })
        .finally(() => { _cartChangeRequests.delete(request); });
      _cartChangeRequests.add(request);
    }, 300);
  }

  /* ============================================================
     Código de descuento en el carrito (planilla CART 30 y 31)
     ------------------------------------------------------------
     Shopify no ofrece un endpoint para validar un código sin usarlo, pero
     `/cart/update.js` con `discount` lo aplica y `/cart.js` contesta si es
     `applicable` y cuánto descuenta; con `discount: ''` lo saca. Con eso
     alcanza para resolverlo en el carrito en vez de mandar al comprador al
     checkout a probar suerte. No se usa `/discount/<code>`: deja el código en
     la sesión y el checkout lo vuelve a aplicar aunque se haya sacado del
     carrito (staging, 24/09).
     ============================================================ */
  function promoError(text) {
    $$('[data-promo-error]').forEach((el) => { el.textContent = text || ''; el.hidden = !text; });
  }

  // "Ingresar código" (Lovable 28/09) abre el campo; sin código aplicado y
  // sin error a la vista, el campo vuelve a plegarse en el enlace.
  function showPromoField(open) {
    $$('[data-promo-link]').forEach((el) => { el.hidden = open; });
    $$('[data-promo-field]').forEach((el) => { el.hidden = !open; });
  }
  function announcePromo(text) {
    $$('[data-promo-live]').forEach((el) => { el.textContent = text; });
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-promo-open]');
    if (!link) return;
    event.preventDefault();
    showPromoField(true);
    const box = link.closest('[data-promo-form]');
    const input = box && box.querySelector('[data-promo-input]');
    if (input) input.focus();
  });

  function renderPromo(cart) {
    const applied = (cart && cart.discount_codes || []).find((d) => d.applicable);
    const amount = (cart && cart.total_discount) || 0;
    $$('[data-promo-applied]').forEach((el) => { el.hidden = !applied; });
    $$('[data-promo-form]').forEach((el) => { el.hidden = !!applied; });
    $$('[data-promo-code]').forEach((el) => { el.textContent = applied ? applied.code : ''; });
    // Con código el monto va en su fila; esta queda para los automáticos.
    $$('[data-cart-discount-row]').forEach((el) => { el.hidden = !(amount > 0) || !!applied; });
    $$('[data-cart-discount]').forEach((el) => { el.textContent = '− ' + money(amount); });
    // Lovable (7), 13/10: el Subtotal es siempre el monto base, sin tachado,
    // y el descuento del código va como −$X en su fila.
    const original = cart && (cart.original_total_price != null ? cart.original_total_price : (cart.total_price || 0) + amount);
    $$('[data-cart-original]').forEach((el) => { el.textContent = money(original || 0); });
    $$('[data-cart-code-amount]').forEach((el) => { el.textContent = '−' + money(amount); });
    if (applied) { promoError(''); $$('[data-promo-input]').forEach((el) => { el.value = ''; }); showPromoField(false); }
  }
  window.__pptRenderPromo = renderPromo;

  async function applyPromo(code) {
    const value = String(code || '').trim();
    if (!value) return;
    const run = ++promoRun;
    promoError('');
    $$('[data-promo-apply]').forEach((el) => { el.disabled = true; });
    try {
      await updateCart({ discount: value });
      if (run !== promoRun) return;
      const cart = await refreshCart(true);
      if (run !== promoRun) return;
      const asked = (cart && cart.discount_codes || []).find((d) => String(d.code).toLowerCase() === value.toLowerCase());
      // Un código que no aplica queda igual en la sesión: se saca para que no
      // se arrastre hasta el checkout ni ensucie el próximo intento.
      if (asked && asked.applicable) announcePromo('Código ' + asked.code + ' aplicado. Total ' + money(cart.total_price || 0) + '.');
      if (!asked || !asked.applicable) {
        await updateCart({ discount: '' });
        if (run !== promoRun) return;
        // No era un descuento: puede ser una tarjeta de regalo (26/10).
        const card = await addGiftCard(value);
        if (run !== promoRun) return;
        if (card) {
          $$('[data-promo-input]').forEach((el) => { el.value = ''; });
          showPromoField(false);
          announcePromo('Tarjeta de regalo terminada en ' + card.lastCharacters + ' aplicada.');
        } else {
          promoError('El código ingresado no es válido');
        }
      }
    } catch (err) {
      if (run === promoRun) promoError('No pudimos aplicar el código. Reintentá.');
    } finally {
      $$('[data-promo-apply]').forEach((el) => {
        const field = el.closest('[data-promo]')?.querySelector('[data-promo-input]');
        el.disabled = !(field && field.value.trim());
      });
    }
  }

  async function removePromo() {
    const run = ++promoRun;
    promoError('');
    const removed = (cartState && cartState.discount_codes || []).find((d) => d.applicable);
    try {
      await updateCart({ discount: '' });
      if (run !== promoRun) return;
      await refreshCart(true);
      if (run === promoRun && removed) announcePromo('Código ' + removed.code + ' quitado.');
    } catch (err) {
      if (run === promoRun) promoError('No pudimos quitar el código. Reintentá.');
    }
  }

  /* ============================================================
     Tarjetas de regalo en el carrito (26/10)
     ------------------------------------------------------------
     El checkout acepta tarjetas de regalo en el campo del código, el carrito
     no: la Ajax API sólo conoce descuentos. La Storefront API sí
     (cartGiftCardCodesAdd, 2025-10) y opera sobre el MISMO carrito: su id es
     el token de /cart.js. Funciona sin token de acceso desde el dominio de la
     tienda, así que no hace falta ninguna app. La tarjeta queda en el carrito
     y el checkout la trae aplicada.

     /cart.js no las informa: la Storefront API se consulta sólo si en esta
     sesión se aplicó alguna (sessionStorage), para no sumar una consulta a
     cada apertura del carrito.
     ============================================================ */
  const GIFT_FLAG = 'ppt-gift-cards';
  let giftCards = [];
  let giftSeq = 0;
  // Sin argumento lee la marca; con true/false la pone o la saca.
  function giftFlag(on) {
    try {
      if (on === true) sessionStorage.setItem(GIFT_FLAG, '1');
      if (on === false) sessionStorage.removeItem(GIFT_FLAG);
      return sessionStorage.getItem(GIFT_FLAG) === '1';
    } catch (_) {
      return on === true;
    }
  }
  async function storefront(query, variables) {
    const res = await fetch('/api/2025-10/graphql.json', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error('Storefront API ' + res.status);
    const body = await res.json();
    if (body.errors && body.errors.length) throw new Error(body.errors[0].message);
    return body.data;
  }
  const GIFT_FIELDS = 'appliedGiftCards { id lastCharacters amountUsed { amount } }';
  const cartGid = (cart) => cart && cart.token ? 'gid://shopify/Cart/' + cart.token : null;
  const giftAmount = (card) => Math.round(parseFloat((card.amountUsed && card.amountUsed.amount) || '0') * 100);

  function renderGiftCards() {
    const used = giftCards.reduce((sum, card) => sum + giftAmount(card), 0);
    $$('[data-gift-cards]').forEach((box) => {
      const tpl = box.querySelector('template');
      $$('[data-gift-card]', box).forEach((row) => row.remove());
      giftCards.forEach((card) => {
        const row = tpl ? tpl.content.firstElementChild.cloneNode(true) : document.createElement('div');
        row.setAttribute('data-gift-card', card.id);
        const last = row.querySelector('[data-gift-last]');
        if (last) last.textContent = '•••• ' + card.lastCharacters;
        const amount = row.querySelector('[data-gift-amount]');
        if (amount) amount.textContent = '−' + money(giftAmount(card));
        box.appendChild(row);
      });
      box.hidden = giftCards.length === 0;
    });
    // Lo que se paga en el checkout: la tarjeta cubre parte (o todo) del total.
    if (cartState) $$('[data-cart-subtotal]').forEach((el) => { el.textContent = money(Math.max((cartState.total_price || 0) - used, 0)); });
  }

  async function loadGiftCards(cart) {
    const id = cartGid(cart);
    if (!id || !giftFlag()) { if (giftCards.length) { giftCards = []; renderGiftCards(); } return; }
    const seq = ++giftSeq;
    try {
      const data = await storefront('query($id: ID!) { cart(id: $id) { ' + GIFT_FIELDS + ' } }', { id });
      if (seq !== giftSeq) return;
      giftCards = (data.cart && data.cart.appliedGiftCards) || [];
      if (!giftCards.length) giftFlag(false);
      renderGiftCards();
    } catch (err) {
      console.error('[PlusPet] gift cards', err);
    }
  }

  // Devuelve la tarjeta aplicada, o null si el código no es una tarjeta válida
  // (Shopify no lo dice con un error: simplemente no aparece aplicada).
  async function addGiftCard(code) {
    const id = cartGid(cartState);
    if (!id) return null;
    try {
      const before = new Set(giftCards.map((card) => card.id));
      const data = await storefront('mutation($id: ID!, $codes: [String!]!) { cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $codes) { cart { ' + GIFT_FIELDS + ' } userErrors { message } } }', { id, codes: [code] });
      const result = data.cartGiftCardCodesAdd;
      const cards = (result && result.cart && result.cart.appliedGiftCards) || [];
      const tail = code.replace(/\s+/g, '').slice(-4).toLowerCase();
      const added = cards.find((card) => !before.has(card.id) && String(card.lastCharacters).toLowerCase() === tail);
      giftSeq++;
      giftCards = cards;
      if (cards.length) giftFlag(true);
      renderGiftCards();
      return added || null;
    } catch (err) {
      console.error('[PlusPet] gift card', err);
      return null;
    }
  }

  async function removeGiftCard(cardId) {
    const id = cartGid(cartState);
    if (!id) return;
    const card = giftCards.find((c) => c.id === cardId);
    try {
      const data = await storefront('mutation($id: ID!, $ids: [ID!]!) { cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $ids) { cart { ' + GIFT_FIELDS + ' } userErrors { message } } }', { id, ids: [cardId] });
      giftSeq++;
      giftCards = (data.cartGiftCardCodesRemove && data.cartGiftCardCodesRemove.cart && data.cartGiftCardCodesRemove.cart.appliedGiftCards) || [];
      if (!giftCards.length) giftFlag(false);
      renderGiftCards();
      if (card) announcePromo('Tarjeta de regalo terminada en ' + card.lastCharacters + ' quitada.');
    } catch (err) {
      promoError('No pudimos quitar la tarjeta. Reintentá.');
    }
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-gift-remove]');
    if (!btn) return;
    const row = btn.closest('[data-gift-card]');
    if (row) removeGiftCard(row.getAttribute('data-gift-card'));
  });
  // Cada render del carrito (cantidades, vaciar) cambia cuánto usa la tarjeta.
  document.addEventListener('ppt:cart-rendered', (event) => {
    if (giftCards.length) renderGiftCards();
    loadGiftCards(event.detail || cartState);
  });

  // Si la página ya carga con un código aplicado, el casillero lo dice sin
  // esperar a que el comprador abra el carrito.
  if (cartState) renderPromo(cartState);

  // Quitar el código vive fuera del casillero: la X de la etiqueta en los
  // totales y "Cambiar" en el detalle de Entrega y horario.
  $$('[data-promo-remove]').forEach((el) => el.addEventListener('click', removePromo));

  // El casillero es markup fijo (se repite en el drawer y en /cart): se engancha
  // directo en cada caja, sin delegar en document.
  $$('[data-promo]').forEach((box) => {
    const input = box.querySelector('[data-promo-input]');
    const enviar = () => applyPromo(input ? input.value : '');
    const apply = box.querySelector('[data-promo-apply]');
    // Lovable 28/09: "Aplicar" deshabilitado mientras el campo está vacío.
    const syncApply = () => { if (apply) apply.disabled = !(input && input.value.trim()); };
    apply?.addEventListener('click', enviar);
    input?.addEventListener('input', () => { promoError(''); syncApply(); });
    syncApply();
    input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      enviar();
    });
  });

  // Barra de incentivo (envío gratis + 3 cuotas). Mantiene texto, tramos e
  // hitos sincronizados con el subtotal real del carrito. Ver
  // snippets/cart-incentive-bar (Lovable 28/09, CartIncentiveBar.tsx).
  //
  // Lo que mide la barra: el subtotal ANTES de los códigos de descuento. El
  // checkout decide el envío gratis con ese monto (con un código del 100 % el
  // envío igual sale gratis) y Lovable mide igual; con el total a pagar la
  // barra bajaba a $0 y apagaba un envío gratis que después se cobraba gratis.
  function incentiveAmount(cart) {
    if (!cart) return 0;
    if (cart.original_total_price != null) return cart.original_total_price;
    return (cart.total_price || 0) + (cart.total_discount || 0);
  }
  window.__pptIncentiveAmount = incentiveAmount;

  // Ancho del primer tramo (0 → envío gratis) sobre el total de la barra. Con
  // un umbral de envío raro (igual o mayor que el de cuotas) no se come todo.
  const incentiveBasis = (shipping, goal) => (goal > 0 ? Math.min(90, (shipping / goal) * 100) : 0);
  const clampPct = (n) => Math.min(Math.max(n, 0), 100);

  // Los tres estados del renglón (Lovable 28/09), con punto final. El mismo
  // texto, sin marcado, es el aria-valuetext.
  // totalCents: subtotal antes de los códigos (decide el envío gratis, como el
  // checkout). payingCents: lo que se cobra (decide las cuotas, como Mercado
  // Pago; pedido 05/10). Sin código son iguales.
  function incentiveMessage(totalCents, shipping, goal, payingCents = totalCents) {
    const gotShipping = shipping > 0 && totalCents >= shipping;
    const reached = payingCents >= goal;
    const remaining = Math.max(0, gotShipping ? goal - payingCents : shipping - totalCents);
    if (reached) return {
      html: 'Tenés <strong class="is-ok">envío gratis</strong> y <strong class="is-ok">3 cuotas sin interés</strong>.',
      text: 'Tenés envío gratis y 3 cuotas sin interés.',
    };
    if (gotShipping) return {
      html: '<strong class="is-ok">¡Envío gratis!</strong> Sumá <strong>' + moneyRound(remaining) + '</strong> y alcanzá <strong>3 cuotas sin interés</strong>.',
      text: '¡Envío gratis! Sumá ' + moneyRound(remaining) + ' y alcanzá 3 cuotas sin interés.',
    };
    return {
      html: 'Sumá <strong>' + moneyRound(remaining) + '</strong> para alcanzar el <strong>envío gratis</strong>.',
      text: 'Sumá ' + moneyRound(remaining) + ' para alcanzar el envío gratis.',
    };
  }

  const incentiveMotion = new WeakMap();
  function animateIncentive(box, fills, percentages, basis) {
    const progress = values => basis * values[0] / 100 + (100 - basis) * values[1] / 100;
    const target = progress(percentages);
    let state = incentiveMotion.get(box);
    if (!state) {
      state = { progress: progress(fills.map(fill => parseFloat(fill?.style.width) || 0)), target: null, frame: null };
      incentiveMotion.set(box, state);
    }
    if (state.target === target && state.basis === basis) return;
    if (state.frame != null) cancelAnimationFrame(state.frame);
    state.target = target;
    state.basis = basis;
    const valuesAt = value => [basis > 0 ? clampPct(value / basis * 100) : 100,
      basis < 100 ? clampPct((value - basis) / (100 - basis) * 100) : 0];
    const finish = () => {
      fills.forEach((fill, i) => {
        if (!fill) return;
        fill.style.width = percentages[i] + '%';
        fill.style.clipPath = '';
      });
      state.progress = target;
      state.frame = null;
    };
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || typeof requestAnimationFrame !== 'function' || state.progress === target) {
      finish();
      return;
    }
    const from = state.progress;
    // Reserve the larger endpoint once. Only the clipping front changes per
    // frame, so both segments share one clock without animating their layout.
    const bounds = valuesAt(from).map((value, i) => Math.max(value, percentages[i]));
    const paint = value => {
      state.progress = value;
      const visible = valuesAt(value);
      fills.forEach((fill, i) => {
        if (fill) fill.style.clipPath = `inset(0 ${bounds[i] ? clampPct(100 - visible[i] / bounds[i] * 100) : 100}% 0 0 round 999px)`;
      });
    };
    paint(from);
    fills.forEach((fill, i) => { if (fill) fill.style.width = bounds[i] + '%'; });
    let started;
    const tick = time => {
      if (!box.isConnected) { state.frame = null; return; }
      if (started == null) started = time;
      const t = Math.min(1, (time - started) / 500);
      const eased = t * t * (3 - 2 * t);
      paint(from + (target - from) * eased);
      if (t === 1) finish();
      else state.frame = requestAnimationFrame(tick);
    };
    state.frame = requestAnimationFrame(tick);
  }

  function updateIncentive(totalCents, payingCents = totalCents) {
    $$('[data-incentive]').forEach((box) => {
      const goal = parseInt(box.getAttribute('data-incentive-goal') || '0', 10);
      const shipping = parseInt(box.getAttribute('data-incentive-shipping') || '0', 10);
      if (!goal) return;

      const gotShipping = shipping > 0 && totalCents >= shipping;
      const reached = payingCents >= goal;
      // Beneficios alcanzados en el resumen del pedido (Lovable 28/09).
      $$('[data-benefit="shipping"]').forEach((el) => { el.hidden = !gotShipping; });
      $$('[data-benefit="installments"]').forEach((el) => { el.hidden = !reached; });
      // Lovable (7): la línea de chips no ocupa lugar si no hay ninguno.
      $$('[data-benefit-chips]').forEach((el) => { el.hidden = !(gotShipping || reached); });

      // Tramos: cada uno se llena por su cuenta y en orden.
      const basis = incentiveBasis(shipping, goal);
      const seg1 = shipping > 0 ? clampPct((totalCents / shipping) * 100) : 100;
      const seg2 = goal > shipping ? clampPct(((payingCents - shipping) / (goal - shipping)) * 100) : (reached ? 100 : 0);
      const segShip = box.querySelector('[data-incentive-seg="shipping"]');
      const segInst = box.querySelector('[data-incentive-seg="installments"]');
      if (segShip) segShip.style.flexBasis = basis + '%';
      if (segInst) segInst.style.flexBasis = (100 - basis) + '%';
      const fillShip = box.querySelector('[data-incentive-fill="shipping"]');
      const fillInst = box.querySelector('[data-incentive-fill="installments"]');
      animateIncentive(box, [fillShip, fillInst], [seg1, seg2], basis);

      const message = incentiveMessage(totalCents, shipping, goal, payingCents);
      const msg = box.querySelector('[data-incentive-msg]');
      if (msg) msg.innerHTML = message.html;
      const track = box.querySelector('[data-incentive-track]');
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(Math.min(gotShipping ? payingCents : totalCents, goal) / 100)));
        track.setAttribute('aria-valuetext', message.text);
      }

      const stopShip = box.querySelector('[data-incentive-stop="shipping"]');
      if (stopShip) stopShip.style.left = basis + '%';
      const marcar = (sel, ok) => {
        box.querySelectorAll(sel).forEach((el) => {
          if (ok) el.dataset.reached = '1';
          else delete el.dataset.reached;
        });
      };
      marcar('[data-incentive-stop="shipping"]', gotShipping);
      marcar('[data-incentive-stop="installments"]', reached);

      if (reached) box.setAttribute('data-incentive-reached', '1');
      else box.removeAttribute('data-incentive-reached');
    });
  }

  // Expuesto para los tests: la barra se sincroniza con el subtotal real y no
  // hay otra forma de ejercitarla sin levantar todo el carrito.
  window.__pptUpdateIncentive = updateIncentive;

  function renderCart(cart) {
    keepCartOrder(cart);
    syncProductStock();
      const dr = $('[data-cart-drawer]');
      const wrap = $('[data-cart-items]');
      const miniWrap = $('[data-cart-items-mini]');
      const countEls = $$('[data-cart-count]');
      const subtotalEls = $$('[data-cart-subtotal]');
      const emptyEl = $('[data-cart-empty]');
      const countTitle = $('[data-cart-count-title]');
      const countMini = $('[data-cart-count-mini]');

      countEls.forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      const cbRate = wrap ? (parseFloat(wrap.getAttribute('data-cart-cashback-rate') || '0') || 0) : 0;
      // Misma cuenta que el backoffice al acreditar: sin los productos
      // excluidos y al peso hacia abajo sobre el total (montos en centavos).
      const cbExcluded = wrap ? (wrap.getAttribute('data-cart-cashback-excluded') || '').split(',').filter(Boolean) : [];
      let cbTotal = 0;
      if (cbRate > 0) {
        const cbBase = cart.items.reduce((sum, it) => (cbExcluded.includes(String(it.product_id)) ? sum : sum + it.final_line_price), 0);
        cbTotal = Math.floor(cbBase * cbRate / 10000) * 100;
      }
      // El cliente paga el TOTAL; el reintegro se acredita después (PDF v4).
      subtotalEls.forEach((el) => { el.textContent = money(cart.total_price); });
      $$('[data-cart-reintegro]').forEach((el) => { el.textContent = money(cbTotal); });
      renderPromo(cart);
      $$('[data-cart-cbbox]').forEach((el) => { el.hidden = !(cbTotal > 0); });
      updateIncentive(incentiveAmount(cart), cart.total_price);
      // Se emite en el drawer (y burbujea hasta document) para que cada picker
      // escuche al carrito que le corresponde y no a uno que ya no existe.
      (cartDrawerEl || document).dispatchEvent(new CustomEvent('ppt:cart-rendered', { detail: cart, bubbles: true }));
      if (countTitle) countTitle.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
      if (countMini) countMini.textContent = cart.item_count + (cart.item_count === 1 ? ' producto' : ' productos');

      // Estado vacío es autoritativo: setCartView respeta el flag.
      const isEmpty = cart.item_count === 0;
      if (dr) dr.setAttribute('data-cart-empty-state', isEmpty ? '1' : '0');
      if (emptyEl) emptyEl.hidden = _cartAddPending || !isEmpty;
      const curView = (dr && dr.getAttribute('data-cart-current-view')) || 'items';
      setCartView(isEmpty ? 'items' : curView);

      if (!wrap) return;
      if (isEmpty) {
        wrap.innerHTML = '';
        if (miniWrap) miniWrap.innerHTML = '';
        return;
      }
      const qtyBtn =
        'width:2rem;height:2rem;font-weight:600;display:flex;align-items:center;justify-content:center;color:var(--color-foreground);';
      const cleanOn = wrap.getAttribute('data-clean-title') === '1';
      wrap.innerHTML = cart.items
        .map(
          (it) => {
            // El reintegro ya no se muestra por línea: repetirlo en cada
            // producto sumaba ruido a una lista que ya tiene precio, cantidad
            // y botón de quitar. Va una sola vez, en los totales.
            return `
        <div data-line-key="${it.key}" style="display:flex;gap:0.75rem;">
          <img src="${it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') : ''}" width="80" height="80" style="width:5rem;height:5rem;border-radius:var(--radius-sm);object-fit:cover;background:var(--color-muted-bg);flex-shrink:0;" alt="">
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;">
            <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:0.5rem;">
              <div style="min-width:0;">
                ${it.url ? `<a href="${escapeHtml(it.url)}" style="font-size:0.875rem;font-weight:600;line-height:1.25rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(cartLineTitle(it, cleanOn))}</a>` : `<p style="font-size:0.875rem;font-weight:600;line-height:1.25rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(cartLineTitle(it, cleanOn))}</p>`}
                <p style="font-size:0.75rem;color:var(--color-muted);margin-top:0.125rem;">${escapeHtml(it.variant_title || '')}</p>
              </div>
              <button type="button" data-line-remove ${_cartAddPending ? 'disabled' : ''} aria-label="Quitar" style="color:color-mix(in srgb,var(--color-foreground) 40%,transparent);flex-shrink:0;padding:0.25rem;margin:-0.25rem;background:none;border:0;cursor:pointer;line-height:0;">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>
              </button>
            </div>
            <div style="margin-top:auto;display:flex;align-items:flex-end;justify-content:space-between;gap:0.75rem;padding-top:0.75rem;">
              <div style="display:inline-flex;align-items:center;border:1px solid var(--color-border);border-radius:var(--radius-sm);overflow:hidden;">
                <button type="button" data-qty-dec ${_cartAddPending ? 'disabled' : ''} aria-label="Restar uno" style="${qtyBtn}">−</button>
                <span data-qty class="tabular" style="width:2rem;height:2rem;text-align:center;font-size:.875rem;font-weight:600;display:inline-flex;align-items:center;justify-content:center;">${it.quantity}</span>
                <button type="button" data-qty-inc ${_cartAddPending || availableStock(it.variant_id || it.id) <= 0 ? 'disabled' : ''} aria-label="Sumar uno" style="${qtyBtn}">+</button>
              </div>
              <div style="display:flex;flex-direction:column;align-items:flex-end;gap:0.25rem;">
                <span style="font-size:0.875rem;font-weight:600;line-height:1;" class="tabular">${money(it.final_line_price)}</span>
              </div>
            </div>
          </div>
        </div>`;
          }
        )
        .join('');
      if (miniWrap) {
        miniWrap.innerHTML = cart.items
          .map(
            (it) => `
        <div style="display:flex;align-items:center;gap:0.75rem;padding:0.375rem 0;">
          <img src="${escapeHtml(it.image || '')}" width="40" height="40" alt="" style="width:2.5rem;height:2.5rem;object-fit:cover;border-radius:var(--radius-sm);background:var(--color-muted-bg);flex-shrink:0;">
          <div style="flex:1;min-width:0;">
            <p style="font-size:0.75rem;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(cartLineTitle(it, cleanOn))}</p>
            <p style="font-size:11px;color:color-mix(in srgb,var(--color-foreground) 60%,transparent);">${escapeHtml(it.variant_title || '')}${it.variant_title ? ' · ' : ''}x${it.quantity}</p>
          </div>
          <span class="tabular" style="font-size:0.75rem;font-weight:600;white-space:nowrap;">${money(it.final_line_price)}</span>
        </div>`
          )
          .join('');
      }
  }

  // Delegación: +/- y eliminar dentro del drawer (sobrevive a los re-render de innerHTML).
  const cartItemsWrap = $('[data-cart-items]');
  if (cartItemsWrap) {
    cartItemsWrap.addEventListener('click', (e) => {
      const row = e.target.closest('[data-line-key]');
      if (!row) return;
      const key = row.getAttribute('data-line-key');
      const qtyEl = row.querySelector('[data-qty]');
      const current = parseInt(qtyEl ? qtyEl.textContent : '0', 10) || 0;
      if (e.target.closest('[data-qty-dec]')) optimisticChange(key, Math.max(0, current - 1));
      else if (e.target.closest('[data-qty-inc]')) optimisticChange(key, current + 1);
      else if (e.target.closest('[data-line-remove]')) optimisticChange(key, 0);
    });
  }

  /* ============================================================
     Nav drawer (mobile)
     ============================================================ */
  const navDrawerEl = $('[data-nav-drawer]');
  const navOverlayEl = $('[data-nav-overlay]');
  const navTrigger = $('[data-nav-open]');
  const navDrawer = createDrawer(navDrawerEl, navOverlayEl, {
    closeSelector: '[data-nav-close]',
    trigger: navTrigger,
    keepInteractive: '.ppt-bottomnav, .ppt-header, header',
  });
  if (navDrawer) {
    $$('[data-nav-open]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); if (navDrawer.isOpen()) navDrawer.close(); else navDrawer.open(e); $$('[data-nav-open]').forEach(trigger => trigger.setAttribute('aria-expanded', String(navDrawer.isOpen()))); })
    );
    $$('[data-nav-close]').forEach((b) => b.addEventListener('click', navDrawer.close));
  }

  /* ============================================================
     Accordion del menú mobile: animar el despliegue.

     Un <details> cerrado no renderiza su contenido, así que una transición
     CSS sobre el panel no arranca (no hay estado inicial) y el menú abre de
     golpe. Acá se intercepta el toggle y se anima la altura a mano.

     El atributo name de los <details> ya hace que solo uno quede abierto,
     pero cierra al hermano de golpe: por eso, antes de abrir uno, se cierra
     el que estaba abierto con la misma animación.
     ============================================================ */
  (function initNavAccordion() {
    const accs = $$('.ppt-navacc');
    if (!accs.length) return;
    const DURATION = 250;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const slide = (acc, opening, done) => {
      const panel = acc.querySelector('.ppt-navacc__panel');
      if (!panel) { acc.open = opening; if (done) done(); return; }
      if (reduced) { acc.open = opening; if (done) done(); return; }

      // Para medir el alto real el contenido tiene que estar renderizado.
      acc.open = true;
      const full = panel.scrollHeight;
      panel.style.height = (opening ? 0 : full) + 'px';
      panel.style.transition = 'height ' + DURATION + 'ms ease';
      // Un frame para que el navegador tome la altura inicial como punto de partida.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { panel.style.height = (opening ? full : 0) + 'px'; });
      });
      setTimeout(() => {
        panel.style.transition = '';
        panel.style.height = '';
        acc.open = opening;
        if (done) done();
      }, DURATION);
    };

    accs.forEach((acc) => {
      const summary = acc.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (acc.dataset.animating === '1') return;
        const opening = !acc.open;
        acc.dataset.animating = '1';
        const run = () => slide(acc, opening, () => { delete acc.dataset.animating; });
        if (opening) {
          const openSibling = accs.find((a) => a !== acc && a.open);
          if (openSibling) slide(openSibling, false, run);
          else run();
        } else {
          run();
        }
      });
    });
  })();

  /* ============================================================
     Dropdowns de navegación (escritorio): sincronizar aria-expanded
     con el estado real del menú (que CSS muestra en hover/focus-within)
     y sostener el panel un instante al salir con el puntero.

     El cierre inmediato del :hover hacía casi imposible llegar a una
     opción del panel de marcas: cualquier micro-salida del cursor
     (el hueco entre dos links, el borde del panel) lo cerraba en el
     camino. .is-open extiende la ventana 150ms y se cancela al volver
     a entrar, así que un movimiento normal del mouse ya no lo pierde.
     ============================================================ */
  // Menú del usuario logueado: CSS lo abre en hover/focus, acá solo se
  // sincroniza aria-expanded para lectores de pantalla.
  $$('[data-user-trigger]').forEach((btn) => {
    const wrap = btn.closest('.ppt-userwrap');
    if (!wrap) return;
    const set = (open) => btn.setAttribute('aria-expanded', String(open));
    wrap.addEventListener('mouseenter', () => set(true));
    wrap.addEventListener('mouseleave', () => set(false));
    wrap.addEventListener('focusin', () => set(true));
    wrap.addEventListener('focusout', (e) => { if (!wrap.contains(e.relatedTarget)) set(false); });
  });

  (function initCategories() {
    const wrap = $('[data-categories]'), trigger = $('[data-categories-trigger]');
    const panel = $('#PptCategories');
    if (!wrap || !trigger || !panel) return;
    let timer, openedByHover = false;
    function set(open) {
      clearTimeout(timer);
      panel.hidden = !open;
      if (!open) openedByHover = false;
      trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        const first = panel.querySelector('[data-nav-dropdown]');
        if (!panel.querySelector('.is-open') && first) {
          first.classList.add('is-open');
          first.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'true');
        }
      }
    }
    trigger.addEventListener('click', e => {
      // The pointer enters before its click. Keep that first activation open;
      // subsequent clicks and keyboard activation still toggle normally.
      const keepOpen = openedByHover && e.detail > 0;
      openedByHover = false;
      set(keepOpen || panel.hidden);
    });
    wrap.addEventListener('mouseenter', () => {
      if (panel.hidden) openedByHover = true;
      set(true);
    });
    wrap.addEventListener('mouseleave', () => { timer = setTimeout(() => set(false), 180); });
    wrap.addEventListener('focusout', e => { if (!wrap.contains(e.relatedTarget)) set(false); });
    document.addEventListener('click', e => { if (!wrap.contains(e.target)) set(false); });
    wrap.addEventListener('keydown', e => {
      if (e.key === 'Escape') { set(false); trigger.focus(); }
      if (e.key === 'ArrowDown' && e.target === trigger) {
        e.preventDefault(); set(true); panel.querySelector('a')?.focus();
      }
    });
  })();

  const NAV_CLOSE_DELAY = 150;
  $$('[data-nav-dropdown]').forEach((item) => {
    const trigger = item.querySelector('[data-nav-trigger]');
    if (!trigger) return;
    let closeTimer = null;
    const set = (open) => {
      trigger.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-open', open);
    };
    const open = () => {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      $$('[data-nav-dropdown]').filter(other => other !== item).forEach(other => {
        other.classList.remove('is-open');
        other.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      });
      set(true);
    };
    const closeSoon = () => {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(() => { closeTimer = null; set(false); }, NAV_CLOSE_DELAY);
    };
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', closeSoon);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) {
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        set(false);
      }
    });
  });

  /* ============================================================
     Entrega (reemplazo de Zapiet): App Proxy zona → fecha → franja.
     Consulta el backend de entrega (pluspet-api → backoffice-api) y
     persiste cart attributes _pluspet_* que valida la Cart Validation
     Function en el checkout y procesa Delivery::ConfirmFromOrder.
     No hay reserva de cupo: la franja no tiene tope de pedidos, así que
     elegirla sólo la deja escrita en el carrito.
     ============================================================ */
  (function initDelivery() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;

    const PROXY = (drawer.dataset.proxy || 'https://api.pluspet.com.ar/proxy').replace(/\/$/, '');
    const SHOP = drawer.dataset.shop || '';
    const CART_TOKEN = drawer.dataset.cartToken || '';

    const postalStep = $('[data-step-postal]');
    const postalInput = $('[data-postal]');
    const postalBtn = $('[data-postal-submit]');
    const zoneSelect = $('[data-zone-select]');
    const dateEl = $('[data-delivery-date]');
    const windowsWrap = $('[data-windows]');
    const msgEl = $('[data-delivery-msg]');
    const postalMsg = $('[data-postal-error]');
    // El backend determina qué tienda cubre el CP. Cambiar sigue usando el
    // selector real del header: nunca reemplazar su destino por uno mock.
    const citySuggest = $('[data-city-suggest]');
    function hideCitySuggestion() { if (citySuggest) citySuggest.hidden = true; }
    function showCitySuggestion(other) {
      if (!citySuggest || !other || !other.name) return false;
      // El nombre operativo del depósito puede cambiar sin cambiar la ciudad.
      const cityName = other.name.trim().replace(/^dep[oó]sito\s+/i, '');
      const currentEl = document.querySelector('[data-city-current]');
      const current = currentEl ? currentEl.textContent.trim() : '';
      if (current && current === cityName) return false;
      const target = [...document.querySelectorAll('[data-city-select]')].find((el) => el.getAttribute('data-city-label') === cityName);
      $('[data-city-suggest-q]', citySuggest).textContent = 'Ingresaste un código de ' + cityName + '.';
      const yes = $('[data-city-suggest-yes]', citySuggest);
      yes.textContent = 'Cambiar a la tienda de ' + cityName;
      yes.hidden = !target;
      yes.onclick = () => { if (target) target.click(); };
      citySuggest.hidden = false;
      return true;
    }
    const statusEl = $('[data-delivery-status]');
    const dayNum = $('[data-step-day-num]');
    const timeNum = $('[data-step-time-num]');
    const methodNum = $('[data-step-method-num]');
    const postalNum = $('[data-step-postal-num]');
    const pickupList = $('[data-pickup-list]');
    // Lo guardado en el carrito que todavía falta volver a poner en pantalla
    // (planilla CART 32): la zona y la franja llegan recién con su consulta.
    let restoreZoneId = null;
    let restoreWindow = null;
    let restored = false;
    const PIN_SVG = '<svg class="ppt-step__pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>';
    const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    let reserved = false;

    // Toda la decisión (qué pasos, qué días, qué texto, qué consulta) vive en
    // assets/delivery-logic.js, que se carga como script clásico antes que este
    // archivo y se testea con vitest. Acá sólo queda el DOM.
    const L = window.PlusPetDelivery;
    if (!L) { console.error('[PlusPet] falta delivery-logic.js'); return; }

    const stepEl = (key) => drawer.querySelector('[data-step="' + key + '"]');
    const pad2 = (n) => String(n).padStart(2, '0');
    const todayISO = () => { const d = new Date(); return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); };

    // Estado del picker. Se declara ANTES del datepicker porque el calendario
    // consulta `fulfillment` para saber qué días puede ofrecer.
    let method = null; // 'shipping' | 'pickup'
    // `options` son zonas (envío) o ubicaciones (retiro): desde la migración a
    // paridad con Zapiet el retiro se elige por ubicación, no por zona.
    let options = [], option = null, slotDate = null, windows = [];
    let fulfillment = L.normalizeFulfillment(null);
    let editingStep = null;   // key del paso que el usuario reabrió con "Editar"
    let selectedWin = null;   // ventana horaria elegida (para el resumen)
    let optionsRun = 0, windowsRun = 0, selectionRun = 0;
    // Guardado del horario en curso (optimista): "Ir a pagar" lo espera.
    let pendingSlotSave = null;
    let confirmedPostal = '', reconfirmDay = false, reconfirmTime = false;

    // Piso inicial: bloquea fechas pasadas antes de conocer la configuración.
    if (dateEl) {
      const bounds = L.dateBounds({ fulfillment: fulfillment, today: todayISO() });
      dateEl.min = bounds.min;
      if (bounds.max) dateEl.max = bounds.max; else dateEl.removeAttribute('max');
    }

    // ---- Días (Lovable 28/09): tarjetas con los próximos días que la agenda
    // permite Y que tienen franjas. Reemplaza al calendario desplegable. Son
    // cuatro, salvo que la ubicación tenga el tope en días hábiles (p. ej.
    // "6 días hábiles" en el backoffice): entonces son esos.
    // Las franjas que se consultan para decidir qué días mostrar quedan en
    // `dayCache`: al elegir el día se pintan sin volver a preguntar.
    const dayGrid = $('[data-day-grid]');
    const dayNote = $('[data-day-note]');
    const DEFAULT_DAYS_SHOWN = 4;
    let daysShown = DEFAULT_DAYS_SHOWN;
    const DAYS_LOOKAHEAD = 30;
    const WD_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    let dayCache = {};
    let dayList = [];
    let daysLoading = false;
    let daysRun = 0;
    const isoAdd = (iso, n) => { const a = iso.split('-'); const d = new Date(+a[0], +a[1] - 1, +a[2] + n); return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); };
    const isoDate = (iso) => { const a = String(iso || '').split('-'); return a.length === 3 ? new Date(+a[0], +a[1] - 1, +a[2]) : null; };
    const dayShort = (iso) => { const d = isoDate(iso); return d ? WD_SHORT[d.getDay()] : ''; };
    // Lovable (4) titula "Hoy" y "Mañana" a las dos primeras tarjetas; acá por
    // fecha, porque pasado el corte la primera ya es mañana.
    const dayHeading = (iso) => {
      const today = todayISO();
      if (iso === today) return 'hoy';
      if (iso === isoAdd(today, 1)) return 'mañana';
      return dayShort(iso);
    };
    const dayNumber = (iso) => { const d = isoDate(iso); return d ? d.getDate() + ' ' + MONTH_SHORT[d.getMonth()] : ''; };
    const windowLabel = (w) => (w ? w.start_time + ' – ' + w.end_time : '');

    function dayNoteText(text) {
      if (!dayNote) return;
      dayNote.textContent = text || '';
      dayNote.hidden = !text;
    }

    function renderDays() {
      if (!dayGrid) return;
      if (daysLoading && !dayList.length) {
        dayGrid.innerHTML = Array.from({ length: daysShown }, () => '<span class="ppt-day ppt-day--skeleton" aria-hidden="true"></span>').join('');
        dayGrid.setAttribute('aria-busy', 'true');
        return;
      }
      dayGrid.removeAttribute('aria-busy');
      const first = dayList.indexOf(slotDate) === -1 ? 0 : dayList.indexOf(slotDate);
      dayGrid.innerHTML = dayList.map((iso, i) => {
        // Con el aviso de reconfirmar el día sigue marcado (Lovable): se
        // confirma tocándolo de nuevo.
        const checked = iso === slotDate;
        return '<button type="button" class="ppt-day" role="radio" data-day="' + iso + '" aria-checked="' + checked + '"' +
          ' tabindex="' + (i === first ? '0' : '-1') + '" aria-label="' + escapeHtml(fmtDayEs(iso)) + '">' +
          '<span class="ppt-day__wd">' + dayHeading(iso) + '</span><span class="ppt-day__num">' + dayNumber(iso) + '</span></button>';
      }).join('');
    }

    // Franjas en píldoras (Lovable 28/09). Sin día elegido se muestran las del
    // primer día, apagadas: dicen qué horarios hay sin dejar elegirlos.
    function renderPills(list, opts) {
      if (!windowsWrap) return;
      const disabled = !!(opts && opts.disabled);
      const chosen = selectedWin ? L.windowKey(selectedWin) : null;
      if (!list.length) { windowsPlaceholder(disabled ? 'Elegí un día para ver los horarios.' : 'Esa fecha no tiene horarios disponibles.'); return; }
      windowsWrap.innerHTML = list.map((w, i) => {
        const key = L.windowKey(w);
        const checked = key === chosen;
        return '<button type="button" class="ppt-slot" role="radio" data-window-option="' + escapeHtml(key) + '"' +
          ' aria-checked="' + checked + '"' + (disabled ? ' disabled' : '') +
          ' tabindex="' + (checked || (!chosen && i === 0) ? '0' : '-1') + '">' + escapeHtml(windowLabel(w)) + '</button>';
      }).join('');
    }
    function renderIdlePills() {
      const sample = dayCache[dayList[0]] || [];
      renderPills(sample, { disabled: true });
    }

    async function fetchDayWindows(date, requestedOption, requestedMethod, variantIds) {
      const req = L.windowsRequest({ method: requestedMethod, shop: SHOP, optionId: requestedOption.id, date: date, variantIds: variantIds });
      const data = await api(req.path, { query: req.query });
      return L.offerableWindows(data && data.windows);
    }

    async function refreshDays() {
      const run = ++daysRun;
      const requestedOption = option;
      const requestedMethod = method;
      const current = () => run === daysRun && requestedOption === option && requestedMethod === method;
      dayCache = {};
      dayList = [];
      dayNoteText('');
      if (!requestedOption || !fulfillment.datePickerEnabled) { daysLoading = false; renderDays(); return; }
      daysLoading = true;
      renderDays();
      if (!slotDate) windowsPlaceholder('Buscando horarios…', true);
      const today = todayISO();
      const candidates = [];
      for (let k = 0; k < DAYS_LOOKAHEAD; k++) {
        const iso = isoAdd(today, k);
        if (L.dayState(iso, fulfillment, today).available) candidates.push(iso);
      }
      // Con el tope en días hábiles el backoffice dice cuántos ofrecer y
      // `dayState` ya dejó afuera lo que queda después del tope.
      daysShown = fulfillment.daysShown || DEFAULT_DAYS_SHOWN;
      let failed = false;
      const variantIds = await cartVariantIds();
      if (!current()) return;
      // De a tres consultas, en orden, hasta juntar los días con franjas.
      for (let k = 0; k < candidates.length && dayList.length < daysShown; k += 3) {
        const batch = candidates.slice(k, k + 3);
        const results = await Promise.all(batch.map((date) =>
          fetchDayWindows(date, requestedOption, requestedMethod, variantIds).catch(() => { failed = true; return null; })));
        if (!current()) return;
        batch.forEach((date, n) => {
          if (!results[n]) return;
          dayCache[date] = results[n];
          if (results[n].length && dayList.length < daysShown) dayList.push(date);
        });
      }
      // Lo que ya estaba elegido (volver del checkout) no se pierde aunque haya
      // quedado más allá de los cuatro primeros: ocupa el último lugar.
      if (slotDate && dayList.indexOf(slotDate) === -1 && L.dayState(slotDate, fulfillment, today).available) {
        let saved = dayCache[slotDate];
        if (!saved) saved = await fetchDayWindows(slotDate, requestedOption, requestedMethod, variantIds).catch(() => null);
        if (!current()) return;
        if (saved && saved.length) {
          dayCache[slotDate] = saved;
          dayList = dayList.slice(0, daysShown - 1).concat(slotDate).sort();
        }
      }
      daysLoading = false;
      renderDays();
      if (!dayList.length) {
        dayNoteText(failed ? 'No pudimos consultar los días disponibles. Reintentá en unos segundos.' : 'Por ahora no hay días con horarios disponibles.');
        windowsPlaceholder('—');
      } else if (!slotDate) {
        renderIdlePills();
      } else if (reconfirmDay && dayCache[slotDate]) {
        // Cambió el depósito: se ve lo elegido, con el aviso, hasta reconfirmar.
        renderPills(dayCache[slotDate], { disabled: true });
      }
    }

    function chooseDay(iso) {
      if (!iso || !dateEl) return;
      const sameDay = iso === slotDate && !reconfirmDay;
      if (sameDay && windows.length) return;
      dateEl.value = iso;
      // Cambiar de día deselecciona el horario: la franja era del día anterior.
      if (!sameDay && !reconfirmTime) selectedWin = null;
      loadWindows(iso);
    }

    const DELIV_MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const DELIV_WDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    function fmtDayEs(iso) {
      const a = (iso || '').split('-'); if (a.length !== 3) return iso || '';
      const d = new Date(+a[0], +a[1] - 1, +a[2]);
      return DELIV_WDAYS[d.getDay()] + ' ' + d.getDate() + ' de ' + DELIV_MONTHS[d.getMonth()];
    }
    function stepSummary(key) {
      // En retiro el resumen es el depósito elegido (con su dirección). En
      // envío, sólo el CP como en Lovable 28/09 (el usuario pidió sacar la
      // zona, 28/09; antes la mostraba por la planilla CART 25).
      if (key === 'postal') {
        if (method === 'pickup') return option ? [option.name, option.detail].filter(Boolean).join(' · ') : '';
        if (confirmedPostal) return 'CP ' + confirmedPostal;
        return option ? L.optionLabel(option) : '';
      }
      // Lovable 28/09: "mié 30 sep · 10:00 – 13:00", sólo con la selección completa.
      if (key === 'day') {
        const parts = [];
        if (slotDate) parts.push(dayShort(slotDate) + ' ' + dayNumber(slotDate));
        if (selectedWin && fulfillment.timePickerEnabled) parts.push(windowLabel(selectedWin));
        else if (selectedWin && method === 'pickup') parts.push('Horario de atención: ' + windowLabel(selectedWin));
        return parts.join(' · ');
      }
      return '';
    }

    // Dos cards (Lovable 28/09): 1 = código postal o depósito, 2 = día y
    // horario. Una sola abierta; las completas colapsan a resumen + "Editar".
    // "Modo resumen": tocar afuera con las dos completas las colapsa a las
    // dos. Tocar afuera con una incompleta la colapsa con su error a la vista.
    let summaryMode = false;
    let outsideAttempt = false;
    const stepDone = () => ({
      postal: option !== null,
      day: reserved && !reconfirmDay && !reconfirmTime && (!fulfillment.datePickerEnabled || !!slotDate),
    });
    const OUTSIDE_ERRORS = () => ({
      postal: method === 'pickup' ? 'Debes seleccionar un depósito.' : 'Debes ingresar un código postal.',
      day: method === 'pickup' ? 'Seleccioná día y horario de retiro.' : 'Debes seleccionar día y horario de entrega.',
    });
    function renderSteps() {
      const done = stepDone();
      const steps = $$('[data-step]', drawer).filter((el) => !el.hidden && !el.hasAttribute('data-step-info'));
      // CART 57: el depósito único es un dato, no un paso.
      $$('[data-step][data-step-info]', drawer).forEach((el) => {
        el.classList.remove('is-active', 'is-done', 'is-locked', 'is-warn', 'is-error', 'has-error');
        el.classList.add('is-info');
        const sumEl = el.querySelector('[data-step-summary]');
        if (sumEl) sumEl.textContent = stepSummary(el.dataset.step);
        const head = el.querySelector('[data-step-edit]');
        if (head) head.disabled = true;
      });
      $$('[data-step].is-info:not([data-step-info])', drawer).forEach((el) => el.classList.remove('is-info'));
      // Al progresar, limpiamos cualquier error de paso pintado por el intento de checkout.
      steps.forEach((el) => el.classList.remove('has-error'));
      let activeIdx = steps.findIndex((el) => !done[el.dataset.step]);
      if (editingStep) {
        const ei = steps.findIndex((el) => el.dataset.step === editingStep);
        if (ei >= 0) activeIdx = ei;
      }
      // Lovable: con todo completo la card 2 sigue abierta mostrando lo elegido;
      // se pliega recién al tocar afuera (modo resumen).
      if (activeIdx === -1 && !editingStep) activeIdx = steps.findIndex((el) => el.dataset.step === 'day');
      if (summaryMode || outsideAttempt) activeIdx = -1;
      steps.forEach((el, i) => {
        const key = el.dataset.step;
        const num = el.querySelector('.ppt-step__num');
        const sumEl = el.querySelector('[data-step-summary]');
        const prevDone = steps.slice(0, i).every((p) => done[p.dataset.step]);
        let state = (i === activeIdx) ? 'active' : (done[key] ? 'done' : (outsideAttempt && prevDone ? 'error' : 'locked'));
        // Como Lovable: un aviso por vez, primero el día y después el horario.
        const warning = key !== 'day' ? '' : reconfirmDay
          ? 'Cambió la zona de entrega: confirmá de nuevo el día.'
          : reconfirmTime ? 'Cambió la zona de entrega: confirmá de nuevo el horario.' : '';
        if (warning && state === 'done') state = 'warn';
        el.classList.toggle('has-warning', !!warning);
        el.classList.remove('is-active', 'is-done', 'is-locked', 'is-warn', 'is-error');
        const warningEl = el.querySelector('[data-step-warning]');
        if (warningEl) { warningEl.textContent = warning; warningEl.hidden = !warning; }
        el.classList.add('is-' + state);
        // Completo aunque siga abierta (la card 2 queda abierta con lo elegido).
        el.toggleAttribute('data-complete', !!done[key]);
        if (state === 'error') setStepError(el, OUTSIDE_ERRORS()[key]);
        if (num) {
          num.classList.remove('is-active', 'is-done');
          if (state === 'done' || state === 'warn') { num.classList.add('is-done'); num.innerHTML = CHECK_SVG; }
          else { num.textContent = i + 1; if (state === 'active') num.classList.add('is-active'); }
        }
        if (sumEl) {
          const text = state === 'done' || state === 'warn' ? stepSummary(key) : '';
          // Lovable 28/09: el CP confirmado lleva un pin adelante.
          if (text && key === 'postal' && method === 'shipping') sumEl.innerHTML = PIN_SVG + escapeHtml(text);
          else sumEl.textContent = text;
        }
        // Una card que se puede reabrir se toca entera (Lovable: el encabezado es un botón).
        const head = el.querySelector('[data-step-edit]');
        if (head) head.disabled = state === 'active' || state === 'locked';
      });
      renderDays();
    }
    const renumberSteps = renderSteps; // alias por compatibilidad con llamadas existentes
    function setEditing(key) {
      summaryMode = false;
      outsideAttempt = false;
      editingStep = key;
      renderSteps();
    }

    // Tocar el encabezado de una card completa (o con error) la reabre.
    drawer.addEventListener('click', (e) => {
      const head = e.target.closest('[data-step-edit]');
      if (!head) return;
      const el = head.closest('[data-step]');
      if (!el || !(el.classList.contains('is-done') || el.classList.contains('is-warn') || el.classList.contains('is-error'))) return;
      // La card 2 no usa editingStep: se abre sola cuando la 1 está completa.
      setEditing(el.dataset.step === 'day' ? null : el.dataset.step);
    });

    // Tocar afuera de las cards (resumen, total, botón) las colapsa: a las dos
    // si están completas, o con el error de la incompleta como ayuda.
    const deliveryRoot = $('[data-delivery-root]', drawer);
    drawer.addEventListener('click', (e) => {
      if (!deliveryRoot || drawer.getAttribute('data-cart-current-view') !== 'delivery') return;
      if (!(e.target instanceof Node) || deliveryRoot.contains(e.target) || !drawer.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-cart-back], [data-cart-close]')) return;
      if (method === null) return;
      const done = stepDone();
      if (done.postal && done.day) { if (summaryMode) return; summaryMode = true; outsideAttempt = false; }
      else { outsideAttempt = true; summaryMode = false; }
      editingStep = null;
      renderSteps();
    }, true);

    // Item 6 — feedback claro al intentar continuar sin completar la entrega.
    // Marca el primer paso incompleto con borde/número danger + mensaje (igual a lovable).
    // El paso 2 junta día y horario: el mensaje dice qué falta de los dos.
    function stepErrorFor(key) {
      if (key === 'postal') return method === 'pickup' ? 'Elegí un depósito para retirar.' : 'Ingresá un código postal válido de 4 dígitos.';
      if (key === 'day') {
        if (fulfillment.datePickerEnabled && !slotDate) return method === 'pickup' ? 'Elegí el día en que querés retirarlo.' : 'Elegí el día en que querés recibirlo.';
        return 'Elegí una franja horaria.';
      }
      return 'Completá este paso para continuar.';
    }
    function setStepError(stepEl, message) {
      stepEl.classList.add('has-error');
      let errEl = stepEl.querySelector('[data-step-error]');
      if (!errEl) {
        errEl = document.createElement('p');
        errEl.className = 'ppt-step__error';
        errEl.setAttribute('data-step-error', '');
        errEl.setAttribute('role', 'alert');
        stepEl.appendChild(errEl);
      }
      errEl.textContent = message;
    }
    function firstIncompleteStep() {
      const done = stepDone();
      return $$('[data-step]', drawer).filter((el) => !el.hidden).find((el) => !done[el.dataset.step]) || null;
    }
    function showStepProblem(key, message) {
      // Día y horario comparten la card 2.
      if (key === 'time') key = 'day';
      const el = stepEl(key);
      if (!el) return;
      summaryMode = false;
      outsideAttempt = false;
      editingStep = key === 'day' ? null : key;   // abrir el paso con problemas
      renderSteps();       // limpia errores previos; el paso queda activo
      setStepError(el, message);
      if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Planilla CART 31: lo elegido pudo dejar de valer mientras el carrito
    // estaba abierto o el comprador estaba en el checkout (la fecha pasó, la
    // franja cerró). Antes de salir se vuelve a preguntar. Devuelve el paso a
    // rehacer, o null si se puede seguir. Si la consulta falla no se frena la
    // compra: la Cart Validation Function del checkout revisa lo mismo.
    async function revalidateDelivery() {
      if (slotDate && !L.dayState(slotDate, fulfillment, todayISO()).available) {
        clearDay();
        return { key: 'day', message: 'Esa fecha ya no está disponible. Elegí otra.' };
      }
      const variantIds = await cartVariantIds();
      const req = L.windowsRequest({ method: method, shop: SHOP, optionId: option.id, date: slotDate, variantIds: variantIds });
      let data;
      try { data = await api(req.path, { query: req.query }); } catch (err) { return null; }
      const fresh = L.offerableWindows(data && data.windows);
      // Lo recién consultado reemplaza a lo que se guardó al armar los días.
      if (slotDate) dayCache[slotDate] = fresh;
      if (!fresh.length) {
        dayList = dayList.filter((d) => d !== slotDate);
        clearDay();
        windowsPlaceholder('Esa fecha ya no tiene horarios disponibles. Elegí otro día.');
        return { key: fulfillment.datePickerEnabled ? 'day' : 'time', message: 'Esa fecha ya no tiene horarios disponibles. Elegí otro día.' };
      }
      const still = selectedWin && fresh.find((w) => String(w.id) === String(selectedWin.id) &&
        w.start_time === selectedWin.start_time && w.end_time === selectedWin.end_time);
      if (!still) {
        await loadWindows(slotDate);
        return { key: 'time', message: 'Ese horario ya no está disponible. Elegí otro.' };
      }
      // Lo que se ve es lo que viaja: si el carrito perdió o cambió la entrega
      // (otra pestaña, un error al guardar), se vuelve a escribir.
      const cart = await getCart();
      try {
        await saveAttributes(L.cartAttributes({
          method: method, shop: SHOP, optionId: option.id, windowId: still.id,
          slotStart: still.start_time, slotEnd: still.end_time, date: slotDate,
          cartToken: (cart && cart.token) || CART_TOKEN || '', postalCode: confirmedPostal,
          location: option.location,
        }));
      } catch (err) { /* lo que ya estaba guardado lo revisa el checkout */ }
      return null;
    }

    const deliveryForm = drawer.querySelector('[data-cart-footer="delivery"] form');
    if (deliveryForm) {
      let revalidated = false;
      let checking = false;
      deliveryForm.addEventListener('submit', async (e) => {
        if (revalidated) { revalidated = false; return; } // ya revisado → checkout nativo
        e.preventDefault();
        // El horario se marca antes de guardarse: se espera ese guardado. Si
        // falla, el paso vuelve a quedar incompleto y se avisa abajo.
        if (pendingSlotSave) { try { await pendingSlotSave; } catch (err) { /* lo avisa selectWindow */ } }
        const missing = firstIncompleteStep();
        if (missing) {
          const key = missing.dataset.step;
          let message = stepErrorFor(key);
          if (key === 'postal') {
            const pc = postalInput ? postalInput.value.trim() : '';
            if (/^\d{4,}$/.test(pc)) message = 'No pudimos calcular la entrega para ese código postal.';
          }
          showStepProblem(key, message);
          return;
        }
        if (checking) return;
        checking = true;
        const button = e.submitter || deliveryForm.querySelector('[type="submit"]');
        if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
        let problem = null;
        try { problem = await revalidateDelivery(); } catch (err) { problem = null; }
        checking = false;
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        if (problem) { showStepProblem(problem.key, problem.message); return; }
        revalidated = true;
        // requestSubmit con el botón manda name="checkout": /cart redirige al
        // checkout como si el comprador lo hubiera tocado.
        if (deliveryForm.requestSubmit) deliveryForm.requestSubmit(button || undefined);
        else { revalidated = false; window.location.href = '/checkout'; }
      });
    }

    // `where` dice de qué paso nace el mensaje. Los del paso del código postal
    // van pegados al campo y nunca al pie del acordeón: un error lejos del
    // control que lo produjo no se lee como propio (planilla CART 18/26/27,
    // mismo patrón que lovable). Sin `where` el destino sigue siendo el pie.
    function msg(text, kind, where) {
      [msgEl, postalMsg].filter(Boolean).forEach(el => {
        el.hidden = true;
        el.textContent = '';
        el.classList.remove('ppt-delivery-loading');
      });
      markPostalInvalid(false);
      if (!text) return;
      const target = where === 'postal' && postalMsg ? postalMsg : msgEl;
      if (!target) return;
      if (target === postalMsg && kind !== 'loading' && kind !== 'ok') markPostalInvalid(true);
      target.hidden = false;
      target.textContent = text;
      target.classList.toggle('ppt-delivery-loading', kind === 'loading');
      target.setAttribute('role', kind === 'loading' || kind === 'ok' ? 'status' : 'alert');
      target.style.color = kind === 'ok' ? 'var(--color-success)' : kind === 'loading' ? 'var(--color-muted)' : 'var(--color-danger)';
    }

    // El campo con error se pinta como tal (lovable usa border-danger), así el
    // aviso y el control que lo produjo se leen juntos.
    function markPostalInvalid(invalid) {
      const ctrls = postalStep && postalStep.querySelector('[data-postal-controls]');
      if (ctrls) ctrls.classList.toggle('is-invalid', !!invalid);
      if (postalInput) {
        if (invalid) postalInput.setAttribute('aria-invalid', 'true');
        else postalInput.removeAttribute('aria-invalid');
      }
    }

    function windowsPlaceholder(text, loading = false) {
      if (windowsWrap) windowsWrap.innerHTML = '<p role="status" class="text-muted' + (loading ? ' ppt-delivery-loading' : '') + '" style="font-size:0.8125rem;">' + text + '</p>';
    }

    // URLSearchParams serializa un array como "1,2" en una sola clave; el proxy
    // espera `variant_ids[]=1&variant_ids[]=2` para poder leerlo como lista.
    function buildQuery(query) {
      const params = new URLSearchParams();
      Object.keys(query).forEach((key) => {
        const value = query[key];
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key + '[]', v));
        } else {
          params.append(key, value);
        }
      });
      return params.toString();
    }

    // F37: qué hay en el carrito. El backoffice lo necesita para descartar
    // métodos que algún producto no admite (un refrigerado que sólo se retira,
    // o algo que ese local no tiene). Si falla, se sigue sin restricción: no
    // poder leer el carrito no puede impedir comprar.
    async function cartVariantIds() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return [];
        const cart = await res.json();
        return (cart.items || []).map((i) => String(i.variant_id)).filter(Boolean);
      } catch (err) {
        return [];
      }
    }

    async function api(path, opts) {
      opts = opts || {};
      const qs = opts.query ? '?' + buildQuery(opts.query) : '';
      const init = { method: opts.method || 'GET', headers: {} };
      if (opts.body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(opts.body);
      }
      const res = await fetch(PROXY + path + qs, init);
      if (!res.ok) throw new Error('proxy ' + res.status);
      if (res.status === 204) return null;
      return res.json();
    }

    let quoteSeq = 0;
    let quoteKey = null;
    let quoteAddress = '';
    // Liquid no conoce el umbral (lo decide el backoffice), así que cada página
    // nueva dibujaba la barra sin el hito y éste aparecía recién cuando el
    // carrito terminaba de cotizar (planilla CART 19 y 20). El último umbral
    // conocido viaja entre páginas y se pinta de entrada; la cotización nueva
    // lo confirma o lo corrige. localStorage y no sessionStorage: una pestaña
    // nueva también es "otra página".
    const THRESHOLD_KEY = 'pluspet:free-shipping-threshold:' + SHOP;
    function rememberThreshold(threshold) {
      try { localStorage.setItem(THRESHOLD_KEY, JSON.stringify(threshold)); } catch (err) { /* sin storage: se cotiza igual */ }
    }
    function rememberedThreshold() {
      try {
        const raw = localStorage.getItem(THRESHOLD_KEY);
        return raw === null ? undefined : JSON.parse(raw);
      } catch (err) { return undefined; }
    }
    // Ni el camión ni las cuotas desaparecen nunca (pedido del usuario,
    // 24/09): sólo un umbral real lo mueve. `undefined` (todavía no se sabe) y
    // `null` (zona sin envío gratis, carrito vacío) dejan el que ya está
    // dibujado —el último conocido o el de referencia del tema— y no se
    // guardan para las próximas páginas.
    function showShippingQuote(text, threshold) {
      if (text !== null) $$('[data-shipping-quote]').forEach(el => { el.textContent = text; });
      if (!(Number.isFinite(threshold) && threshold > 0)) return;
      rememberThreshold(threshold);
      $$('[data-free-shipping-amount]').forEach(el => { el.textContent = money(threshold); });
      $$('[data-incentive]').forEach(box => {
        const shipping = threshold;
        box.dataset.incentiveShipping = String(shipping);
        const stop = box.querySelector('[data-incentive-stop="shipping"]');
        const goal = Number(box.dataset.incentiveGoal);
        if (stop) {
          stop.hidden = false;
          // Un umbral fuera de escala queda cerca del final, no se esconde.
          if (goal) stop.style.left = incentiveBasis(shipping, goal) + '%';
        }
        const amount = box.querySelector('[data-shipping-threshold]');
        if (amount) amount.textContent = shipping ? money(shipping) : '';
      });
      if (cartState) updateIncentive(incentiveAmount(cartState), cartState.total_price);
    }
    async function refreshShippingQuote(cart) {
      // Sin carrito todavía no hay nada que cotizar ni que decidir sobre el
      // hito: lo que esté dibujado se queda hasta que llegue el carrito real.
      if (!cart) return;
      // Opening the cart on another page must retain the saved destination's
      // incentive, without pretending that the delivery picker is completed.
      const savedMethod = cart?.attributes?._pluspet_method;
      const quoteMethod = method === null ? (savedMethod || 'delivery') : method === 'pickup' ? 'pickup' : 'delivery';
      const quotePostal = method === null ? L.normalizePostal(cart?.attributes?._pluspet_address_postal_code || '') : confirmedPostal;
      const hasDestination = method === null
        ? quoteMethod === 'pickup' || (quoteMethod === 'delivery' && L.postalState(quotePostal).valid)
        : !!option;
      const previewThreshold = quoteMethod === 'delivery' && !quotePostal && !quoteAddress;
      const payload = cart && (hasDestination || previewThreshold) ? {
        shop: SHOP, fulfillment_method: quoteMethod,
        postal_code: quotePostal, address: quoteAddress,
        subtotal_cents: cart.total_price, weight_grams: cart.total_weight,
        item_count: cart.item_count, shopify_variant_ids: (cart.items || []).map(i => String(i.variant_id)),
        include_threshold: '1',
      } : null;
      const key = JSON.stringify(payload);
      if (key === quoteKey) return;
      quoteKey = key;
      const seq = ++quoteSeq;
      if (!cart.item_count) { showShippingQuote('Elegí la entrega para consultar su costo.', null); return; }
      // Sin destino todavía (el CP recién confirmado, la zona en camino) no se
      // sabe nada nuevo del umbral: borrarlo hacía parpadear el hito.
      if (!payload) { showShippingQuote('Elegí la entrega para consultar su costo.'); return; }
      showShippingQuote('Consultando costo de entrega…');
      try {
        const result = await api('/shipping_rates', { query: payload });
        if (seq !== quoteSeq) return;
        if (previewThreshold) {
          // The backend proves a common threshold for all covered zones;
          // rates stay empty until a destination is provided.
          showShippingQuote('Elegí la entrega para consultar su costo.', result?.free_shipping_threshold_cents);
          return;
        }
        const rates = result && result.rates || [];
        // Sin tarifa para el destino el umbral igual vale: es la misma promesa
        // para toda la cobertura y el comprador puede cambiar el destino.
        if (!rates.length) { showShippingQuote('No hay una tarifa disponible para esta compra y destino.', quoteMethod === 'pickup' ? undefined : result?.free_shipping_threshold_cents); return; }
        const price = Math.min(...rates.map(rate => Number(rate.price_cents)));
        if (!Number.isFinite(price) || price < 0) throw new Error('invalid quote');
        let text = (quoteMethod === 'pickup' ? 'Retiro: ' : 'Envío: ') + (price === 0 ? 'gratis' : (rates.length > 1 ? 'desde ' : '') + money(price));
        const threshold = result.free_shipping_threshold_cents;
        if (quoteMethod !== 'pickup' && Number.isFinite(threshold) && threshold > cart.total_price) text += '. Envío gratis desde ' + money(threshold) + ' para esta zona y productos';
        // En retiro el umbral no se consulta (no hay envío), pero el hito es la
        // misma promesa de siempre: la barra de lovable no depende del método.
        showShippingQuote(text + '. El importe final se confirma al pagar.', quoteMethod === 'pickup' ? undefined : threshold);
      } catch (err) {
        if (seq !== quoteSeq) return;
        quoteKey = null;
        // No poder consultar no prueba que no haya envío gratis: se conserva el
        // hito que ya estaba dibujado.
        showShippingQuote('No pudimos consultar el costo. Se calculará al pagar con tu dirección.');
      }
    }
    const knownThreshold = rememberedThreshold();
    if (knownThreshold !== undefined) showShippingQuote(null, knownThreshold);

    // Volver del checkout (o abrir el carrito en otra página) no puede costar
    // rehacer la entrega: lo elegido sigue escrito en los atributos del
    // carrito, así que se vuelve a poner en pantalla tal cual (planilla CART
    // 32). Sólo se restaura si el comprador todavía no empezó a elegir, para
    // no pisarle nada, y sin escribir: no se guarda lo que ya está guardado.
    function restoreDelivery(cart) {
      if (cart?.item_count === 0 || restored || method !== null) return;
      const attrs = (cart && cart.attributes) || {};
      const saved = attrs._pluspet_method;
      if (!saved) return;
      restored = true;
      method = saved === 'pickup' ? 'pickup' : 'shipping';
      editingStep = null;
      applyMethodChrome();
      restoreZoneId = attrs._pluspet_zone_id || null;
      restoreWindow = attrs._pluspet_window_id
        ? { id: attrs._pluspet_window_id, start: attrs._pluspet_slot_start || '', end: attrs._pluspet_slot_end || '' }
        : null;
      const date = attrs._pluspet_slot_date || '';
      if (date && dateEl) dateEl.value = date;
      applyFulfillment();
      if (method === 'pickup') { loadPickupLocations(); return; }
      const postal = L.normalizePostal(attrs._pluspet_address_postal_code || '');
      if (!L.postalState(postal).valid) { renderSteps(); return; }
      confirmedPostal = postal;
      if (postalInput) postalInput.value = postal;
      syncPostalControls();
      loadDeliveryZones(postal);
    }

    drawer.addEventListener('ppt:cart-rendered', event => {
      if (_cartAddPending) return;
      refreshShippingQuote(event.detail);
      restoreDelivery(event.detail);
    });

    function saveAttributes(attrs, opts) {
      // El orden de los cambios del comprador también manda en Shopify.
      // Serializar evita que una elección vieja termine después de la nueva.
      return updateCart({ attributes: attrs }, opts);
    }

    drawer.addEventListener('ppt:cart-emptied', event => {
      method = null; restored = false; restoreZoneId = null; restoreWindow = null;
      pendingSlotSave = null;
      if (postalInput) postalInput.value = '';
      hideCitySuggestion(); syncPostalControls(); applyMethodChrome();
      resetSelection(event.detail);
    });

    // La configuración de agenda manda sobre la UI: si la ubicación no ofrece
    // selector de fecha o de hora, esos pasos no existen y el acordeón se
    // renumera solo (renderSteps ignora los pasos ocultos).
    function applyFulfillment() {
      renderPickupCards();
      const needsOptionChoice = options.length > 1;
      const visible = L.visibleSteps({ method: method, fulfillment: fulfillment, needsOptionChoice: needsOptionChoice });
      const postalEl = stepEl('postal');
      if (postalEl) postalEl.hidden = visible.indexOf('postal') === -1;
      // La card 2 junta día y horario: existe si la agenda ofrece alguno de
      // los dos, y adentro se ve sólo lo que ofrece.
      const dayEl = stepEl('day');
      if (dayEl) {
        dayEl.hidden = !method || !(fulfillment.datePickerEnabled || fulfillment.timePickerEnabled);
        const dayBlock = dayEl.querySelector('[data-day-block]');
        const timeBlock = dayEl.querySelector('[data-time-block]');
        if (dayBlock) dayBlock.hidden = !fulfillment.datePickerEnabled;
        if (timeBlock) timeBlock.hidden = !fulfillment.timePickerEnabled;
        const title = dayEl.querySelector('[data-step-title]');
        if (title) title.textContent = method === 'pickup'
          ? (fulfillment.datePickerEnabled && fulfillment.timePickerEnabled ? 'Día y horario de retiro' : fulfillment.datePickerEnabled ? 'Día de retiro' : 'Horario de retiro')
          : (fulfillment.datePickerEnabled && fulfillment.timePickerEnabled ? 'Seleccioná día y horario' : fulfillment.datePickerEnabled ? 'Seleccioná el día' : 'Seleccioná el horario');
      }
      if (postalStep && method === 'pickup') {
        // En retiro el paso 2 aloja el selector de ubicación: el control de CP
        // no aplica.
        const ctrls = postalStep.querySelector('[data-postal-controls]');
        if (ctrls) ctrls.hidden = true;
      }
      if (dateEl) {
        const bounds = L.dateBounds({ fulfillment: fulfillment, today: todayISO() });
        dateEl.min = bounds.min;
        if (bounds.max) dateEl.max = bounds.max; else dateEl.removeAttribute('max');
      }
      renumberSteps();
    }

    function resetSelection(cart = cartState) {
      optionsRun++;
      windowsRun++;
      selectionRun++;
      options = []; option = null; slotDate = null; windows = []; reserved = false; selectedWin = null; editingStep = null;
      confirmedPostal = ''; reconfirmDay = false; reconfirmTime = false;
      quoteAddress = '';
      if (dateEl) dateEl.value = '';
      daysRun++; dayCache = {}; dayList = []; daysLoading = false; dayNoteText('');
      if (dayGrid) { dayGrid.innerHTML = ''; dayGrid.removeAttribute('aria-busy'); }
      summaryMode = false; outsideAttempt = false;
      refreshShippingQuote(cart);
      fulfillment = L.normalizeFulfillment(null);
      if (zoneSelect) { zoneSelect.hidden = true; zoneSelect.innerHTML = '<option value="">Elegí una opción…</option>'; }
      if (statusEl) statusEl.hidden = true;
      if (windowsWrap) windowsWrap.innerHTML = '';
      msg('');
      applyFulfillment();
    }

    function onOptionResolved() {
      if (option && option.fulfillment) fulfillment = L.normalizeFulfillment(option.fulfillment);
      refreshShippingQuote(cartState);
      reserved = false; // opción nueva: la elección anterior deja de valer
      if (!reconfirmTime) selectedWin = null;
      editingStep = null;
      // Dónde cae el pedido se lee donde se eligió: el resumen del paso 2 en
      // envío, la card del punto en retiro (planilla CART 25 y 19). Al pie no
      // queda ningún helper suelto.
      msg('');
      applyFulfillment();
      // Sin selector de fecha se piden las franjas de una: no hay día que elegir.
      if (!fulfillment.datePickerEnabled) { loadWindows(null); return; }
      // Los días se consultan de nuevo para esta opción; un día ya elegido
      // (volver del checkout) pide sus franjas en paralelo.
      refreshDays();
      if (!reconfirmDay && dateEl && dateEl.value) loadWindows(dateEl.value);
    }

    function fillOptionSelect(label) {
      if (!zoneSelect) return;
      zoneSelect.innerHTML = '<option value="">' + escapeHtml(label) + '</option>' +
        options.map((o) => '<option value="' + escapeHtml(o.id) + '">' + escapeHtml(L.optionLabel(o)) + '</option>').join('');
      zoneSelect.hidden = false;
      applyFulfillment();
    }

    // autoFirst: para envío a domicilio derivamos la zona del CP automáticamente
    // (igual a lovable: nunca se le pregunta la zona al cliente). El selector
    // sólo se usa para retiro cuando hay varias ubicaciones físicas.
    async function loadOptions(opts) {
      const run = ++optionsRun;
      hideCitySuggestion();
      windowsRun++;
      selectionRun++;
      const requestedMethod = method;
      quoteAddress = opts.address || '';
      option = null;
      refreshShippingQuote(cartState);
      msg('Buscando…', 'loading', 'postal');
      try {
        const variantIds = await cartVariantIds();
        if (run !== optionsRun || requestedMethod !== method) return;
        const data = await api('/delivery_zones', {
          query: L.zonesQuery({
            shop: SHOP, method: requestedMethod, postalCode: opts.postalCode,
            address: opts.address, variantIds: variantIds,
          }),
        });
        if (run !== optionsRun || requestedMethod !== method) return;
        fulfillment = L.normalizeFulfillment(data && data.fulfillment);
        options = L.methodOptions(data, method).options;
        applyFulfillment();

        // Método apagado o bloqueado por el carrito: se dice con palabras en vez
        // de dejar una grilla vacía, que se lee como "está roto".
        const blocked = L.unavailableMessage({
          method: method,
          fulfillment: fulfillment,
          blockedBy: (data && data.blocked_by) || [],
        });
        // Que el método esté apagado no es un error del campo: habla de la
        // elección del paso 1, así que se dice al pie.
        if (blocked) { option = null; options = []; msg(blocked); windowsPlaceholder('—'); applyFulfillment(); return; }

        if (options.length === 0) {
          option = null;
          windowsPlaceholder('—');
          // CART 53: si el CP es de otra ciudad, se ofrece el cambio en vez
          // de decir que no se cubre.
          if (method === 'shipping' && showCitySuggestion(data && data.other_city)) { msg('', null, 'postal'); return; }
          msg(L.emptyOptionsMessage(method), null, 'postal');
          return;
        }
        // Al volver del checkout manda la zona que ya estaba guardada; si dejó
        // de existir, se sigue por el camino normal y el paso se rehace.
        const saved = restoreZoneId && options.find((o) => String(o.id) === String(restoreZoneId));
        restoreZoneId = null;
        if (saved) { option = saved; if (zoneSelect) zoneSelect.hidden = true; onOptionResolved(); return; }
        if (options.length === 1 || opts.autoFirst) { option = options[0]; if (zoneSelect) zoneSelect.hidden = true; onOptionResolved(); return; }
        // En retiro los depósitos ya son cards con radio (Lovable 28/09): ni
        // selector extra ni aviso, la card 1 abierta alcanza.
        if (method === 'pickup') { applyFulfillment(); return; }
        fillOptionSelect(opts.multiLabel); msg(opts.multiMsg, null, 'postal');
      } catch (err) {
        if (run !== optionsRun || requestedMethod !== method) return;
        msg('No se pudo consultar la entrega (¿app no disponible en este entorno?).', null, 'postal');
      }
    }

    // `address` llega sólo cuando el comprador eligió una dirección del
    // buscador: es lo que le permite al backoffice resolver zonas por polígono
    // o por radio, que el código postal solo no alcanza para matchear.
    const loadDeliveryZones = (pc, address) =>
      loadOptions({ postalCode: pc, address: address, multiLabel: 'Elegí una zona…', multiMsg: 'Hay varias zonas: elegí una.', autoFirst: true });
    // Retiro: lo que se elige es una ubicación, no una zona (zones viene vacío).
    const loadPickupLocations = () =>
      loadOptions({ multiLabel: 'Elegí un punto…', multiMsg: 'Elegí dónde querés retirar.', autoFirst: false });

    function clearDay() {
      slotDate = null;
      reserved = false;
      selectedWin = null;
      if (dateEl) dateEl.value = '';
      renderDays();
    }

    async function loadWindows(date) {
      const run = ++windowsRun;
      selectionRun++;
      reserved = false;
      if (!reconfirmTime) selectedWin = null;
      windows = [];
      if (!option) { msg(method === 'pickup' ? 'Primero elegí dónde retirás.' : 'Primero elegí la zona / código postal.'); return; }
      // Con el día deshabilitado por configuración no tiene sentido consultar.
      if (date) {
        const state = L.dayState(date, fulfillment, todayISO());
        // Una fecha que ya no se ofrece (p. ej. la guardada antes de ir al
        // checkout, que ya pasó) no puede quedar escrita en el paso.
        if (!state.available) { clearDay(); windowsPlaceholder(L.unavailableDayMessage(state.reason)); renumberSteps(); return; }
      }
      const requestedOption = option;
      const requestedMethod = method;
      const current = () => run === windowsRun && requestedOption === option && requestedMethod === method;
      slotDate = date;
      reconfirmDay = false;
      reserved = false;
      if (!reconfirmTime) selectedWin = null;
      editingStep = null;
      renumberSteps();
      const cached = date && dayCache[date];
      if (!cached) windowsPlaceholder('Buscando horarios…', true);
      try {
        // F41: el tiempo de preparación puede depender de lo que haya en el
        // carrito (un producto o un tag pueden pedir más margen), así que el
        // backoffice necesita saber qué se está comprando para decidir desde
        // qué horario se puede ofrecer.
        // Las franjas del día ya consultado para armar las tarjetas se reusan.
        if (cached) windows = cached;
        else {
          const variantIds = await cartVariantIds();
          if (!current()) return;
          const req = L.windowsRequest({
            method: requestedMethod, shop: SHOP, optionId: requestedOption.id, date: date, variantIds,
          });
          const data = await api(req.path, { query: req.query });
          if (!current()) return;
          // Una franja sin cupo no se ofrece: elegirla sería prometer algo que el
          // webhook después rechaza.
          windows = L.offerableWindows(data && data.windows);
          if (date) dayCache[date] = windows;
        }
        if (windows.length === 0) {
          clearDay();
          renumberSteps();
          windowsPlaceholder('Esa fecha ya no tiene horarios disponibles. Elegí otro día.');
          return;
        }
        // Selector de hora apagado: el comprador ya eligió el día y no ve
        // franjas, pero el pedido igual viaja con una (el backoffice confirma
        // por window_id).
        const auto = L.autoWindow(windows, fulfillment);
        if (auto) { await selectWindow(L.windowKey(auto)); return; }
        // La franja guardada se vuelve a marcar tal cual estaba: ya está
        // escrita en el carrito, así que no se guarda de nuevo. Si ya no
        // existe, el paso queda abierto en vez de fingir que sigue elegida.
        if (restoreWindow) {
          // El id no alcanza: dos subhorarios pueden compartirlo y se
          // distinguen por su horario, que es lo que quedó en el carrito.
          const want = restoreWindow;
          restoreWindow = null;
          const saved = windows.find((w) => String(w.id) === String(want.id) &&
            (!want.start || (w.start_time === want.start && w.end_time === want.end)));
          if (saved) {
            selectedWin = saved;
            reserved = true;
            reconfirmTime = false;
          }
        }
        // Lovable 28/09: las franjas son píldoras en dos columnas (reemplazan
        // al listbox de la planilla CART 40).
        renderPills(windows);
        renumberSteps();
      } catch (err) {
        if (!current()) return;
        windowsPlaceholder('No se pudieron cargar los horarios.');
      }
    }

    // Carrito VIVO: data-cart-token="{{ cart.token }}" se renderiza al cargar la
    // página y queda stale (o vacío si el carrito estaba vacío y luego se
    // agregaron items por AJAX). /cart.js siempre devuelve el estado actual.
    async function getCart() {
      try {
        return await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      } catch (err) { return null; }
    }

    async function selectWindow(windowId) {
      const win = windows.find((w) => L.windowKey(w) === String(windowId));
      if (!win || reconfirmDay) return;
      const run = ++selectionRun;
      const requestedOption = option;
      const requestedMethod = method;
      const requestedDate = slotDate;
      const current = () => run === selectionRun && requestedOption === option && requestedMethod === method && requestedDate === slotDate;
      // CART 56/58: optimista. La píldora se marca y las cards se pliegan al
      // instante (sin "Guardando horario…"); el guardado va por detrás. Si
      // falla, vuelve la franja anterior, se reabre la card y se avisa. "Ir a
      // pagar" espera a que termine (pendingSlotSave) antes de salir.
      const previous = { win: selectedWin, reserved: reserved, reconfirmTime: reconfirmTime };
      selectedWin = win;
      reserved = true;
      reconfirmTime = false;
      editingStep = null;
      renderPills(windows);
      const done = stepDone();
      if (done.postal && done.day) {
        summaryMode = true;
        outsideAttempt = false;
        drawer.dispatchEvent(new CustomEvent('ppt:delivery-complete'));
      }
      renumberSteps();
      msg('');
      if (statusEl) statusEl.hidden = true;
      const fail = (text) => {
        selectedWin = previous.win;
        reserved = previous.reserved;
        reconfirmTime = previous.reconfirmTime;
        summaryMode = false;
        editingStep = null;
        renderPills(windows);
        renumberSteps();
        msg(text);
      };

      const save = (async () => {
        const cart = await getCart();
        if (!current()) return;
        const cartToken = (cart && cart.token) || CART_TOKEN || '';
        if (!cartToken) {
          // Sin token: distinguir carrito vacío de un fallo transitorio al
          // consultar /cart.js (sí hay productos).
          fail(cart && cart.item_count === 0
            ? 'Agregá un producto al carrito antes de elegir el horario.'
            : 'No pudimos validar tu carrito. Reintentá en unos segundos.');
          return;
        }
        // Ya no hay reserva de franja: no existe cupo máximo, así que elegir
        // un horario es sólo dejarlo escrito en el carrito. El webhook de
        // confirmación revalida zona, método y horario del local.
        const attrs = L.cartAttributes({
          method: method,
          shop: SHOP,
          optionId: option.id,
          windowId: win.id,
          slotStart: win.start_time,
          slotEnd: win.end_time,
          date: slotDate,
          cartToken: cartToken,
          postalCode: confirmedPostal,
          location: option.location,
        });
        try {
          await saveAttributes(attrs);
        } catch (err) {
          if (current()) fail('No se pudo guardar el horario. Reintentá.');
        }
      })();
      pendingSlotSave = save;
      try { await save; } finally { if (pendingSlotSave === save) pendingSlotSave = null; }
    }

    // Planilla CART 19: en retiro el punto deja de vivir en un paso con un
    // selector y pasa a ser una card con su dirección a la vista, elegida con
    // un radio (igual que lovable). Con un solo punto la card es la
    // confirmación de dónde se retira.
    // CART 57: con un solo depósito en la provincia no hay nada que elegir:
    // la card 1 deja de ser un paso (sin número ni "Editar") y queda como
    // dato, "Retirás en …"; día y horario pasa a ser el paso 1. Mientras no
    // se sabe cuántos hay, el título neutro (el caso común es uno).
    function pickupTitle() {
      return options.length > 1 ? 'Seleccioná el depósito' : 'Retirás en';
    }
    function renderPickupCards() {
      if (!pickupList) return;
      const pickupStepTitle = postalStep && postalStep.querySelector('.ppt-step__title');
      if (pickupStepTitle && method === 'pickup') pickupStepTitle.textContent = pickupTitle();
      if (postalStep) postalStep.toggleAttribute('data-step-info', method === 'pickup' && options.length === 1);
      if (method !== 'pickup' || !options.length) {
        pickupList.hidden = true;
        pickupList.innerHTML = '';
        return;
      }
      pickupList.hidden = false;
      // Si ya están pintadas las mismas cards sólo cambia cuál está elegida:
      // reconstruirlas le saca el foco a quien está eligiendo con el teclado.
      const pintadas = [...pickupList.querySelectorAll('[data-pickup-option]')];
      if (pintadas.length === options.length &&
          pintadas.every((el, i) => el.dataset.pickupOption === String(options[i].id))) {
        pintadas.forEach((el) => {
          el.setAttribute('aria-checked', String(!!option && String(option.id) === el.dataset.pickupOption));
        });
        return;
      }
      pickupList.innerHTML = options.map((o) => {
        const chosen = option && String(option.id) === String(o.id);
        return '<button type="button" class="ppt-pickup__card" role="radio" aria-checked="' + (chosen ? 'true' : 'false') + '"' +
          ' data-pickup-option="' + escapeHtml(String(o.id)) + '">' +
          '<span class="ppt-pickup__radio" aria-hidden="true"></span>' +
          '<span class="ppt-pickup__body">' +
            '<span class="ppt-pickup__name">' + escapeHtml(o.name) + '</span>' +
            (o.detail ? '<span class="ppt-pickup__addr">' + escapeHtml(o.detail) + '</span>' : '') +
          '</span></button>';
      }).join('');
    }

    if (pickupList) pickupList.addEventListener('click', (event) => {
      const card = event.target.closest('[data-pickup-option]');
      if (!card) return;
      const chosen = options.find((o) => String(o.id) === card.dataset.pickupOption);
      if (!chosen) return;
      // La ya elegida confirma y cierra la edición del paso, como volver a
      // tocar el método: si no, "Dónde retirás" quedaba abierto sin salida.
      if (option && String(option.id) === String(chosen.id)) { setEditing(null); return; }
      // Lovable 28/09: cambiar de depósito con día u horario elegidos avisa
      // que hay que confirmarlos de nuevo.
      if (option && (slotDate || selectedWin)) { reconfirmDay = !!slotDate; reconfirmTime = !!selectedWin; }
      option = chosen;
      onOptionResolved();
    });


    // Lo que cambia en pantalla al elegir método, sin tocar la selección: lo
    // usan tanto el click como la restauración de una entrega ya guardada.
    function applyMethodChrome() {
      // Control segmentado (Lovable 28/09): radios con un solo tabindex.
      $$('[data-method]').forEach((b) => {
        b.setAttribute('aria-checked', String(b.dataset.method === method));
        b.tabIndex = b.dataset.method === (method || 'shipping') ? 0 : -1;
      });
      if (!postalStep) return;
      // El control de CP vuelve a ser visible por defecto (flujo envío);
      // para retiro lo oculta applyFulfillment.
      const ctrls = postalStep.querySelector('[data-postal-controls]');
      if (ctrls) ctrls.hidden = false;
      // El título del paso 2 cambia según el método: en retiro no se pide
      // un código postal, se elige dónde retirar.
      const title = postalStep.querySelector('.ppt-step__title');
      if (title) title.textContent = method === 'pickup' ? pickupTitle() : 'Ingresá tu código postal';
      // El título de la card 2 lo pone applyFulfillment: depende del método y
      // de lo que ofrezca la agenda.
    }

    function chooseMethod(next, opts) {
      // Re-elegir el método YA elegido no debe resetear nada: abajo se limpia
      // la instrucción de entrega, y borrarla porque el comprador volvió a
      // tocar el mismo botón se ve como que la nota "se perdió".
      if (method === next) { setEditing(null); return; }
      method = next;
      editingStep = null;
      applyMethodChrome();
      resetSelection();
      // Limpia los atributos de una elección anterior: si no, al cambiar de
      // método quedan zona/ventana/slot/CP viejos y el webhook podría
      // confirmar un pedido con datos mezclados. Vaciar _pluspet_window_id
      // hace que el bridge NO sincronice hasta una reserva nueva y consistente.
      saveAttributes({
        _pluspet_method: method === 'pickup' ? 'pickup' : 'delivery',
        _pluspet_zone_id: '',
        _pluspet_window_id: '',
        _pluspet_slot_start: '',
        _pluspet_slot_end: '',
        _pluspet_slot_date: '',
        _pluspet_cart_token: '',
        _pluspet_address_postal_code: '',
        ...Object.fromEntries(zapietAttributeKeys().map((key) => [key, ''])),
      }).catch((err) => console.error('[PlusPet] save attributes', err));
      if (method === 'pickup') loadPickupLocations();
      // Planilla CART 35: elegido el envío, lo que sigue es escribir el
      // código postal. Al entrar a la vista (sin tocar nada) no se roba el foco.
      else if (postalInput && !(opts && opts.quiet)) postalInput.focus();
    }

    $$('[data-method]').forEach((btn) => {
      btn.addEventListener('click', () => chooseMethod(btn.dataset.method));
    });

    // Lovable 28/09: el control segmentado siempre tiene una opción activa y
    // arranca en envío a domicilio. Si el carrito ya trae una entrega guardada
    // (volver del checkout), manda esa.
    drawer.addEventListener('ppt:cart-view', (e) => {
      if (e.detail !== 'delivery' || method !== null) return;
      if (cartState && cartState.attributes && cartState.attributes._pluspet_method) { restoreDelivery(cartState); if (method !== null) return; }
      chooseMethod('shipping', { quiet: true });
    });

    // Radios con flechas (método, depósito, día y horario), como Lovable.
    drawer.addEventListener('keydown', (e) => {
      if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
      const group = e.target.closest && e.target.closest('[role="radiogroup"]');
      if (!group || !drawer.contains(group)) return;
      const radios = [...group.querySelectorAll('[role="radio"]:not([disabled])')];
      const i = radios.indexOf(e.target.closest('[role="radio"]'));
      if (i < 0) return;
      e.preventDefault();
      const next = radios[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1) + radios.length) % radios.length];
      next.focus();
      next.click();
    });

    // --- Buscador de código postal -----------------------------------------

    function submitPostal() {
      const pc = L.normalizePostal(postalInput ? postalInput.value : '');
      if (postalInput) postalInput.value = pc;
      const state = L.postalState(pc);

      // Validar acá evita un viaje y, sobre todo, evita el mensaje "no cubrimos
      // ese código postal" cuando lo que pasa es que no escribió uno.
      if (!state.valid) { msg(state.message || 'Ingresá tu código postal.', null, 'postal'); return; }
      if (state.message) { msg(state.message, null, 'postal'); return; }

      confirmPostal(pc);
    }
    if (postalBtn) postalBtn.addEventListener('click', submitPostal);

    // Enter confirma aunque "Confirmar" siga deshabilitado: con un código
    // incompleto es la forma de enterarse de qué le falta (Lovable).
    if (postalInput) postalInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submitPostal(); }
    });

    if (zoneSelect) zoneSelect.addEventListener('change', () => {
      const chosen = options.find((x) => String(x.id) === zoneSelect.value);
      if (chosen) { option = chosen; onOptionResolved(); }
    });

    // El input es un borrador: ni guardar ni cotizar con él hasta confirmar.
    // Un CP distinto invalida la agenda en Shopify antes de consultar la zona.
    async function confirmPostal(pc, address) {
      if (pc === confirmedPostal && option) {
        editingStep = null;
        msg('');
        renderSteps();
        return;
      }
      // Lovable 28/09: cambió la zona, así que día y horario se reinician en
      // silencio (sin aviso de reconfirmar).
      if (pc !== confirmedPostal) {
        clearDay();
        reconfirmDay = false;
        reconfirmTime = false;
      }
      confirmedPostal = pc;
      const run = ++optionsRun;
      hideCitySuggestion();
      windowsRun++; selectionRun++;
      option = null; windows = []; reserved = false; editingStep = null;
      renderSteps();
      refreshShippingQuote(cartState);
      if (windowsWrap) windowsWrap.innerHTML = '';
      msg('Confirmando código postal…', 'loading', 'postal');
      try {
        await saveAttributes({
          _pluspet_zone_id: '', _pluspet_window_id: '',
          _pluspet_slot_start: '', _pluspet_slot_end: '', _pluspet_slot_date: '',
          _pluspet_cart_token: '', _pluspet_address_postal_code: '',
          ...Object.fromEntries(zapietAttributeKeys().map((key) => [key, ''])),
        });
      } catch (err) {
        if (run === optionsRun) msg('No se pudo confirmar el código postal. Reintentá.', null, 'postal');
        return;
      }
      if (run !== optionsRun || method !== 'shipping') return;
      loadDeliveryZones(pc, address);
    }

    // Lovable 28/09: "Confirmar" se habilita desde 4 caracteres y la X limpia el campo.
    const postalClear = $('[data-postal-clear]');
    function syncPostalControls() {
      const value = postalInput ? postalInput.value.trim() : '';
      if (postalBtn) postalBtn.disabled = value.length < 4;
      if (postalClear) postalClear.hidden = !value;
    }
    if (postalInput) postalInput.addEventListener('input', () => {
      hideCitySuggestion();
      msg('');
      if (postalStep) postalStep.classList.remove('has-error');
      syncPostalControls();
    });
    if (postalClear) postalClear.addEventListener('click', () => {
      if (!postalInput) return;
      postalInput.value = '';
      msg('');
      syncPostalControls();
      postalInput.focus();
    });
    syncPostalControls();

    // Terminar de editar ya es motivo para avisar: no hace falta apretar la
    // lupa para enterarse de que el código postal no sirve (planilla CART 26).
    // Vacío no es error: todavía no escribió nada.
    // Planilla CART 36: con un código válido, salir del campo ya lo confirma.
    // Si se sale tocando "Continuar", confirma el botón: dos consultas por el
    // mismo código se pisaban.
    if (postalInput) postalInput.addEventListener('blur', (event) => {
      const pc = L.normalizePostal(postalInput.value);
      if (!pc) return;
      const state = L.postalState(pc);
      if (!state.valid) { msg(state.message || 'Ingresá tu código postal.', null, 'postal'); return; }
      if (state.message || (postalBtn && event.relatedTarget === postalBtn)) return;
      if (method !== 'shipping' || pc === confirmedPostal) return;
      postalInput.value = pc;
      confirmPostal(pc);
    });

    // Días y franjas (Lovable 28/09): tarjetas y píldoras con role="radio".
    if (dayGrid) dayGrid.addEventListener('click', (e) => {
      const b = e.target.closest('[data-day]');
      if (b && !b.disabled) chooseDay(b.dataset.day);
    });
    if (windowsWrap) windowsWrap.addEventListener('click', (e) => {
      const b = e.target.closest('[data-window-option]');
      if (!b || b.disabled) return;
      // La franja ya elegida sólo cierra lo que se estaba editando.
      if (selectedWin && !reconfirmTime && L.windowKey(selectedWin) === b.dataset.windowOption && reserved) { setEditing(null); return; }
      selectWindow(b.dataset.windowOption);
    });
  })();

  /* ============================================================
     Interceptar "agregar al carrito" para abrir el drawer
     ============================================================ */
  function previewCartAdd(form, data) {
    try {
      const product = JSON.parse($('[data-cart-product]', form)?.textContent || 'null');
      const variant = product?.variants.find(v => String(v.id) === String(data.get('id')));
      const quantity = Number(data.get('quantity') || 1);
      if (!variant || !Number.isFinite(variant.price) || !Number.isInteger(quantity) || quantity < 1 || !cartState) return false;
      if (quantity > availableStock(variant.id) || variant.available === false) return false;
      const cart = JSON.parse(JSON.stringify(cartState));
      const hasProperties = [...data.keys()].some(key => key.startsWith('properties['));
      const existing = !hasProperties && !data.get('selling_plan') && cart.items.find(item =>
        String(item.variant_id) === String(variant.id) && !item.selling_plan_allocation && !Object.keys(item.properties || {}).length);
      if (existing) existing.quantity += quantity;
      else cart.items.push({ key: 'pending-' + variant.id, variant_id: variant.id, quantity,
        product_title: product.title, vendor: product.vendor, variant_title: variant.title === 'Default Title' ? '' : variant.title,
        image: variant.image, price: variant.price, final_price: variant.price });
      recomputeCartTotals(cart);
      cartState = cart;
      return true;
    } catch (_) { return false; }
  }

  function initAddForms(root = document) {
    if (!cartDrawer) return;
    $$('form[action="/cart/add"], form[action$="/cart/add"]', root).forEach((form) => {
      if (form.dataset.cartBound === '1') return;
      form.dataset.cartBound = '1';
      try { rememberStock(JSON.parse($('[data-cart-product]', form)?.textContent || 'null')?.variants || []); } catch (_) {}
      syncProductStock(form);
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (_cartAddPending) return;
        const data = new FormData(form);
        const requested = Number(data.get('quantity') || 1);
        const addButton = e.submitter || form.querySelector('[type="submit"]');
        if (addButton && addButton.dataset.maxReached === '1' && availableStock(data.get('id')) <= 0) {
          notifyMaxStockReached();
          return;
        }
        if (requested > availableStock(data.get('id'))) {
          cartDrawer.open();
          showCartError('Ya alcanzaste el stock disponible de esta presentación. Revisá la cantidad en el carrito.');
          syncProductStock(form);
          return;
        }
        _cartAddPending = true;
        data.set('sections', 'cart-state');
        const pending = $('[data-cart-add-pending]');
        const empty = $('[data-cart-empty]');
        const submitButton = e.submitter || form.querySelector('[type="submit"]');
        if (submitButton) { submitButton.disabled = true; submitButton.setAttribute('aria-busy', 'true'); }
        const beforeAdd = cartState ? JSON.parse(JSON.stringify(cartState)) : null;
        _cartMutSeq++; // Ignore reads and quantity responses started before this preview.
        const previewed = previewCartAdd(form, data);
        if (pending) pending.hidden = previewed;
        if (empty) empty.hidden = true;
        hideCartError();
        cartDrawer.open();
        try {
          // Finish already requested quantity changes before taking the add's
          // snapshot. New quantity changes are paused while the add is pending.
          while (Object.keys(_cartChangeTimers).length) await new Promise(resolve => setTimeout(resolve, 25));
          await Promise.all([..._cartChangeRequests]);
          // A removal response may have been ignored because this add already
          // started its optimistic preview. Confirm emptiness before the POST.
          if (emptyCartReset) await emptyCartReset;
          if (emptyCartToReset || (beforeAdd?.item_count === 0 && (cartHadItems || hasCartOptions(beforeAdd)))) {
            const current = await fetch('/cart.js').then(res => {
              if (!res.ok) throw new Error('No se pudo consultar el carrito.');
              return res.json();
            });
            await confirmCart(current);
          }
          _cartMutSeq++;
          const res = await fetch('/cart/add.js', { method: 'POST', body: data });
          if (!res.ok) {
            const errData = await res.json().catch(() => null);
            const msg = (errData && (errData.description || errData.message)) || 'No se pudo agregar al carrito.';
            cartState = beforeAdd;
            await refreshCart(true);
            showCartError(msg);
            return;
          }
          hideCartError();
          const added = await res.json();
          if (added && added.product_title) notifyProductAdded(added.vendor, cleanTitle(added.product_title, added.vendor, true));
          const section = added.sections && added.sections['cart-state'];
          let cart = null;
          if (section) {
            try {
              const doc = new DOMParser().parseFromString(section, 'text/html');
              cart = withDiscountCodes(JSON.parse(doc.querySelector('[data-cart-state]').textContent), doc,
                (beforeAdd && beforeAdd.discount_codes) || []);
              readCartStock(doc);
            } catch (err) { /* Section rendering can fail independently of add. */ }
          }
          if (cart && Array.isArray(cart.items) && Number.isFinite(cart.total_price)) {
            await confirmCart(cart);
            cartState = cart;
            renderCart(cart);
          } else if (!await refreshCart(true)) throw new Error('No se recibió el carrito confirmado.');
        } catch (err) {
          // A lost response can still mean the add succeeded. Never repeat a
          // non-idempotent add via native submit: reconcile instead.
          cartState = beforeAdd;
          await refreshCart(true);
          showCartError('No pudimos confirmar el agregado. Revisá el carrito antes de volver a intentarlo.');
        } finally {
          _cartAddPending = false;
          if (pending) pending.hidden = true;
          if (submitButton) { submitButton.disabled = false; submitButton.removeAttribute('aria-busy'); }
          if (cartState) renderCart(cartState);
          else { if (empty) empty.hidden = cartDrawerEl.dataset.cartEmptyState !== '1'; setCartView('items'); }
        }
      });
    });
  }
  initAddForms();
  document.addEventListener('ppt:products-loaded', event => initAddForms(event.detail?.root || document));

  // Un título que es un peso simple ("1,5kg", "340gr") manda sobre el peso del
  // catálogo: hay variantes cargadas con un cero de más —"1,5kg" con 15000g— y
  // ahí el precio por kilo salía diez veces más barato, encima con el badge de
  // "Mejor $/kg" en la presentación equivocada.
  //
  // Los títulos compuestos siguen saliendo del catálogo: en "20+2kg" el título
  // dice 2 y el producto pesa 22, así que ahí el que miente es el título.
  function pricePerKg(price, title, grams) {
      const t = String(title || '').trim().toLowerCase().replace(/\s/g, '');
      const m = t.match(/^(\d+(?:[.,]\d+)?)(kg|gr?)$/);
      if (m) {
        const value = Number(m[1].replace(',', '.'));
        if (Number.isFinite(value) && value > 0) return price / (m[2] === 'kg' ? value : value / 1000);
      }
      if (Number(grams) > 0) return price / (Number(grams) / 1000);
      return null;
    }
  // La preselección elige entre las disponibles; la etiqueta "Mejor $/kg" marca
  // la mejor aunque esté agotada (pedido 05/10), así que la pide con includeSoldOut.
  function bestWeightVariant(variants, includeSoldOut = false) {
    return variants.filter(v => (includeSoldOut || v.available) && /\d\s*(?:kg|gr?|gramos?)\b/i.test(v.title || ''))
      .map(v => ({ ...v, perKg: pricePerKg(v.price, v.title, v.grams) }))
      .filter(v => v.perKg !== null).sort((a, b) => a.perKg - b.perKg);
  }
  function addBestWeightBadge(pills, variants, className) {
    const comparable = bestWeightVariant(variants, true);
    if (comparable.length < 2 || comparable[1].perKg - comparable[0].perKg <= 1) return;
    const best = pills.find(pill => String(pill.dataset.id) === String(comparable[0].id));
    if (!best || best.querySelector('.ppt-weight-best')) return;
    const badge = document.createElement('span');
    badge.className = 'ppt-weight-best ' + className;
    badge.textContent = 'Mejor $/kg';
    best.appendChild(badge);
  }
  /* ============================================================
     Product-card: pills de variante (como el diseño lovable).
     Al elegir una presentación, actualiza precio/oferta/CTA y el
     id que se agrega al carrito — sin ir a la ficha. Delegado en
     document para cubrir cards añadidas dinámicamente.
     ============================================================ */
  function selectCardVariant(pill) {
    if (!pill || pill.disabled) return;
    const card = pill.closest('[data-ppt-card]');
    if (!card) return;

    card.querySelectorAll('[data-card-variant]').forEach((p) => {
      const active = p === pill;
      p.classList.toggle('is-active', active);
      p.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const idInput = card.querySelector('[data-card-id]');
    if (idInput) idInput.value = pill.dataset.id;

    const priceEl = card.querySelector('[data-card-price]');
    if (priceEl) priceEl.textContent = pill.dataset.price;

    const oldEl = card.querySelector('[data-card-old]');
    if (oldEl) {
      if (pill.dataset.onSale === '1') { oldEl.textContent = pill.dataset.compare; oldEl.hidden = false; }
      else { oldEl.hidden = true; }
    }

    const offerEl = card.querySelector('[data-card-offer]');
    if (offerEl) {
      if (pill.dataset.onSale === '1') {
        const dEl = offerEl.querySelector('[data-card-discount]');
        if (dEl) dEl.textContent = pill.dataset.discount;
        offerEl.hidden = false;
      } else {
        offerEl.hidden = true;
      }
    }

    const giftEl = card.querySelector('[data-card-gift]');
    if (giftEl) {
      const kg = pill.dataset.bonusKg || '';
      const kgEl = giftEl.querySelector('[data-card-gift-kg]');
      if (kgEl) kgEl.textContent = kg;
      giftEl.hidden = !kg;
    }

    const stockEl = card.querySelector('[data-card-stock]');
    if (stockEl) {
      const txt = pill.dataset.stock || '';
      const txtEl = stockEl.querySelector('[data-card-stock-text]');
      if (txtEl) txtEl.textContent = txt; else stockEl.textContent = txt;
      stockEl.hidden = txt === '';
      stockEl.setAttribute('data-stock-level', pill.dataset.stockLevel || '');
    }

    const addBtn = card.querySelector('[data-card-add]');
    if (addBtn) {
      const avail = pill.dataset.available === '1';
      addBtn.disabled = !avail;
      addBtn.textContent = avail ? 'Agregar' : 'Agotado';
    }

    // Swap de imagen si la variante tiene su propia foto (data real de Shopify).
    const imgEl = card.querySelector('[data-card-img]');
    if (imgEl && pill.dataset.image) {
      updateProductImage(imgEl, pill.dataset.image, pill.dataset.imageWidth);
    }
    syncProductStock(idInput?.form);
  }
  function initCardVariants(root = document) {
    const cards = root.matches?.('[data-ppt-card]') ? [root] : $$('[data-ppt-card]', root);
    const stockCity = document.querySelector('[data-city-current]')?.textContent.trim();
    cards.forEach(card => {
      if (stockCity) $$('[data-card-stock-city]', card).forEach(el => { el.textContent = ' en ' + stockCity; });
      if (card.dataset.variantsInitialized === '1') return;
      card.dataset.variantsInitialized = '1';
      const pills = $$('[data-card-variant]', card);
      const variants = pills.map(pill => ({id: pill.dataset.id,
        available: pill.dataset.available === '1', price: Number(pill.dataset.priceCents),
        grams: Number(pill.dataset.grams), title: pill.textContent}));
      addBestWeightBadge(pills, variants, 'ppt-card__best');
      const initial = card.dataset.selectedVariant || bestWeightVariant(variants)[0]?.id;
      if (initial) selectCardVariant(pills.find(pill => String(pill.dataset.id) === String(initial)));
    });
  }
  document.addEventListener('click', event => selectCardVariant(event.target.closest('[data-card-variant]')));
  document.addEventListener('ppt:products-loaded', event => initCardVariants(event.detail?.root || document));
  initCardVariants();

  /* ============================================================
     Header: se oculta al scrollear hacia abajo, reaparece al subir
     (como el diseño lovable). Con foco, el primer scroll pliega incluso arriba.
     ============================================================ */
  (function initHeaderHide() {
    const header = document.querySelector('.ppt-header');
    if (!header) return;
    // Lovable 23/09: en la ficha de producto la fila de búsqueda arranca
    // plegada y sólo aparece al bajar y volver a subir.
    const isPdp = document.body.classList.contains('template-product');
    if (isPdp) header.classList.add('is-search-collapsed');
    function scrollPosition() {
      const scroller = document.scrollingElement || document.documentElement;
      const limit = scroller.clientHeight > 0 ? Math.max(0, scroller.scrollHeight - scroller.clientHeight) : Infinity;
      // Safari exposes rubber-band positions outside the document. Returning
      // from that bounce is not the shopper scrolling in the other direction.
      return Math.min(limit, Math.max(0, window.scrollY));
    }
    let last = scrollPosition(), lastHeight = header.offsetHeight;
    let distance = 0, direction = 0, ticking = false, idle = null, openingTimer = null;
    let openingSearch = false;
    let searchDismissed = false;
    let touchStart = null, gestureDismissed = false;
    // Punto más alto del dedo en el gesto que cerró la búsqueda (clientY más
    // chico = página más abajo). Volver más de REVERSE_PX desde ahí es una
    // subida deliberada, no inercia del cierre (usuario 04/10, iPhone).
    let lockedTopY = null;
    const REVERSE_PX = 24;
    // iOS (usuario 04/10): al cerrarse el teclado, el primer swipe hacia
    // arriba puede mover sólo el viewport visual, sin cambiar scrollY ni
    // disparar scroll: la página no se entera y la búsqueda quedaba plegada
    // hasta otro "abajo y arriba". El dedo que sube la página más de
    // FINGER_OPEN_PX en un gesto nuevo también la despliega.
    let fingerStartY = null; // punto más alto del dedo en el gesto actual
    const FINGER_OPEN_PX = 40;
    const search = header.querySelector('.ppt-search');
    const headerPanels = $$('.ppt-navdrawer, .ppt-mobile-account');
    let panelBottom = null;
    function syncPanelGeometry() {
      if (!headerPanels.length) return;
      const bottom = Math.round(header.getBoundingClientRect().bottom);
      if (bottom === panelBottom) return;
      panelBottom = bottom;
      // Only these sheets consume this variable. Inheriting a changing value
      // from :root invalidates styles throughout the storefront every frame.
      headerPanels.forEach(panel => panel.style.setProperty('--header-bottom', bottom + 'px'));
    }
    const hasSearchFocus = () => search?.contains(document.activeElement);
    function rememberPosition(rememberHeight = true) {
      last = scrollPosition();
      if (rememberHeight) lastHeight = header.offsetHeight;
    }
    function dismissSearch() {
      clearTimeout(openingTimer);
      openingSearch = false;
      if (search?.contains(document.activeElement)) document.activeElement.blur();
      header.classList.add('is-search-collapsed');
      searchDismissed = true;
      distance = 0;
      rememberPosition();
    }
    function resetScrollIdle() {
      clearTimeout(idle);
      idle = setTimeout(() => { distance = 0; direction = 0; gestureDismissed = false; lockedTopY = null; }, 400);
    }
    // With the Android keyboard open, a finger can pan the visual viewport
    // before window.scrollY changes. Dismiss on the first vertical touch move.
    document.addEventListener('touchstart', event => {
      lockedTopY = null;
      fingerStartY = null;
      touchStart = event.touches.length === 1 && !event.target.closest?.('.ppt-search-results')
        ? { id: event.touches[0].identifier, x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true, capture: true });
    document.addEventListener('touchmove', event => {
      if (gestureDismissed) resetScrollIdle();
      if (fingerStartY !== null && event.touches.length === 1 && event.touches[0].clientY < fingerStartY) {
        fingerStartY = event.touches[0].clientY;
      }
      if (fingerStartY !== null && !gestureDismissed && event.touches.length === 1 &&
          event.touches[0].clientY - fingerStartY > FINGER_OPEN_PX &&
          header.classList.contains('is-search-collapsed') && !(isPdp && scrollPosition() <= 10) &&
          !/(^|\s)(drawer-open|ppt-account-sheet-open|ppt-city-sheet-open)(\s|$)/.test(document.body.className)) {
        fingerStartY = null;
        header.classList.remove('is-search-collapsed');
        searchDismissed = false;
        distance = 0;
        direction = -1;
        rememberPosition();
      }
      // Sólo el gesto que cerró la búsqueda (lockedTopY se arma ahí y se
      // borra al soltar): un toque nuevo lo resuelve el bloque de abajo.
      if (gestureDismissed && lockedTopY !== null && event.touches.length === 1) {
        const fingerY = event.touches[0].clientY;
        if (fingerY < lockedTopY) lockedTopY = fingerY;
        else if (fingerY - lockedTopY > REVERSE_PX) {
          gestureDismissed = false;
          lockedTopY = null;
          distance = 0;
          direction = 0;
        }
      }
      if (!touchStart || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - touchStart.x, dy = touch.clientY - touchStart.y;
      if (touch.identifier !== touchStart.id || dy === 0 || Math.abs(dy) <= Math.abs(dx)) return;
      const touchStart0Y = touchStart.y;
      touchStart = null;
      if (hasSearchFocus()) {
        dismissSearch();
        gestureDismissed = true;
        lockedTopY = touch.clientY;
      } else {
        // A new vertical swipe is intentional input, not inertia left over
        // from the swipe that closed the keyboard. Don't wait for 400ms idle.
        gestureDismissed = false;
        distance = 0;
        direction = 0;
        // Video iOS 04/10: el primer movimiento puede llegar al revés mientras
        // el viewport se reacomoda tras el teclado. Se mide desde el punto más
        // alto del dedo en el gesto, sea cual sea la dirección inicial.
        fingerStartY = Math.min(touchStart0Y, touch.clientY);
      }
      resetScrollIdle();
    }, { passive: true, capture: true });
    const endTouch = () => { touchStart = null; lockedTopY = null; fingerStartY = null; if (gestureDismissed) resetScrollIdle(); };
    document.addEventListener('touchend', endTouch, { passive: true });
    document.addEventListener('touchcancel', endTouch, { passive: true });
    document.addEventListener('wheel', event => {
      if (!hasSearchFocus() || event.deltaY === 0 || Math.abs(event.deltaY) <= Math.abs(event.deltaX) || event.ctrlKey || event.target.closest?.('.ppt-search-results')) return;
      dismissSearch();
      gestureDismissed = true;
      resetScrollIdle();
    }, { passive: true, capture: true });
    function update(fromScroll = false) {
      const y = scrollPosition(), height = header.offsetHeight;
      // Scroll anchoring follows the changing header height. That movement is
      // layout compensation, not the shopper reversing their scroll direction.
      const delta = y - last - (height - lastHeight);
      lastHeight = height;
      const nextDirection = Math.sign(delta);
      // Opening changes layout and the keyboard viewport. Their scroll events
      // must not cancel the user's click. Real touch/wheel gestures still
      // dismiss synchronously in the listeners above.
      if (openingSearch) { last = y; ticking = false; return; }
      // Con Cuenta abierta el fondo no se mueve por el usuario: el bloqueo de
      // scroll no debe desplegar ni plegar la búsqueda.
      if (document.body.classList.contains('ppt-account-sheet-open')) { last = y; ticking = false; return; }
      // Even the first pixel dismisses the keyboard and starts collapsing.
      // Keep layout compensation and suggestion-panel scrolling out of this.
      if (fromScroll && delta !== 0 && hasSearchFocus()) {
        dismissSearch();
        direction = nextDirection;
        ticking = false;
        return;
      }
      if (gestureDismissed) { last = y; ticking = false; return; }
      // Slow swipes can advance less than 2px per frame. Accumulate them too;
      // the 40px threshold and direction reset already filter small jitter.
      if (delta === 0) { last = y; ticking = false; return; }
      if (nextDirection !== direction) distance = 0;
      distance += Math.abs(delta);
      direction = nextDirection;
      // Fuera de la ficha, volver arriba siempre despliega la búsqueda: si
      // la lupa la plegó con pocos píxeles de scroll, arriba no queda
      // recorrido para juntar los 40px y quedaba plegada (usuario, 04/10).
      if (y <= 10 && (!searchDismissed || (delta < 0 && (distance > 40 || !isPdp)))) {
        // PRODUCT 37 (Lovable Header): en la ficha, volver arriba pliega de
        // nuevo la búsqueda para dejar más pantalla; en el resto se muestra.
        header.classList.toggle('is-search-collapsed', isPdp);
        searchDismissed = false;
      } else if (distance > 40) {
        // Bajar sólo pliega (pasados los 60 px) y subir sólo abre, como
        // Lovable. Con toggle(delta > 0 && y > 60), un tirón de más de 40 px
        // antes de los 60 px abría la búsqueda al bajar y un poco más abajo la
        // volvía a cerrar: el "abre y cierra" de la ficha.
        if (delta < 0) {
          header.classList.remove('is-search-collapsed');
          searchDismissed = false;
        } else if (y > 60) header.classList.add('is-search-collapsed');
        distance = 0;
      }
      last = y;
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      // Lo acumulado caduca al frenar: si no, medio gesto de hace rato se suma
      // al siguiente y la barra cambia de estado con un empujón de 5px.
      resetScrollIdle();
      if (hasSearchFocus()) update(true);
      else if (!ticking) { window.requestAnimationFrame(() => update(true)); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', () => {
      // Resize can precede scroll anchoring by a frame. Preserve the height
      // adjustment until scroll consumes it, rather than treating it as a
      // direction reversal and starting another search-row transition.
      rememberPosition(false);
      syncPanelGeometry();
    });
    // La lupa abre y enfoca dentro del click; la animación no cancela el foco.
    const shortcut = header.querySelector('[data-search-shortcut]');
    if (shortcut) shortcut.addEventListener('click', () => {
      header.classList.remove('is-search-collapsed');
      searchDismissed = false;
      gestureDismissed = false;
      const input = header.querySelector('.ppt-search input');
      clearTimeout(openingTimer);
      const duration = search ? Math.max(...getComputedStyle(search).transitionDuration.split(',').map(value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000) || 0)) : 0;
      const mobileKeyboard = window.matchMedia?.('(max-width: 1023px)').matches;
      openingSearch = duration > 0 || mobileKeyboard;
      // iOS needs focus inside the user's click to reliably show its keyboard.
      input?.focus({ preventScroll: true });
      rememberPosition();
      if (openingSearch) openingTimer = setTimeout(() => {
        openingSearch = false;
        rememberPosition();
      }, Math.max(duration + 80, mobileKeyboard ? 320 : 0));
    });
    update();
    syncPanelGeometry();
    // ResizeObserver runs after layout, so measuring here doesn't force a
    // second layout after each scroll-driven class change. Keep a fallback
    // for browsers without it; resize handles viewport changes in both paths.
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(syncPanelGeometry).observe(header);
    else header.addEventListener('transitionend', syncPanelGeometry);
    // Scroll locking can move the header without resizing it. Measure after
    // all click handlers have opened the sheet, before its first painted frame.
    // Account stops propagation in document capture; observe at window first.
    window.addEventListener('click', event => {
      if (event.target.closest?.('[data-nav-open], [data-mobile-account-toggle]')) window.requestAnimationFrame(syncPanelGeometry);
    }, { capture: true });
  })();

  /* ============================================================
     Ficha de producto: dirección de retiro (planilla PRODUCT 25)
     El texto de "Retiro en el depósito" lleva [direccion_retiro] y la
     dirección sale de los puntos de retiro que el backoffice tiene cargados
     para esta tienda. Sin respuesta el lugar queda vacío: no se inventa.
     ============================================================ */
  (function initPdpPickupAddress() {
    const slots = $$('[data-pdp-pickup-address]');
    const L = window.PlusPetDelivery;
    const drawer = $('[data-cart-drawer]');
    if (!slots.length || !L || !drawer?.dataset.proxy) return;
    const query = new URLSearchParams();
    const params = L.zonesQuery({ shop: drawer.dataset.shop, method: 'pickup', variantIds: [$('#PdpVariantId')?.value].filter(Boolean) });
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (Array.isArray(value)) value.forEach((v) => query.append(key + '[]', v));
      else if (value !== undefined && value !== null && value !== '') query.append(key, value);
    });
    fetch(drawer.dataset.proxy.replace(/\/$/, '') + '/delivery_zones?' + query)
      .then((res) => { if (!res.ok) throw new Error('delivery_zones ' + res.status); return res.json(); })
      .then((data) => {
        const places = L.methodOptions(data, 'pickup').options.map((o) => o.detail || o.name).filter(Boolean);
        if (!places.length) return;
        slots.forEach((el) => { el.textContent = '(' + places.join(' o ') + ')'; el.hidden = false; });
      })
      .catch(() => {});
  })();

  /* ============================================================
     Ficha de producto: selector de variantes
     ============================================================ */
  (function initPdpVariants() {
    const dataEl = $('#PdpVariantData');
    if (!dataEl) return;
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const pills = $$('[data-pdp-variant]');
    const idInput = $('#PdpVariantId');
    const qtyInput = $('#PdpQty');

    const byId = (id) => data.variants.find((v) => String(v.id) === String(id));
    const shippingBox = $('[data-pdp-shipping]');
    const shippingPostal = $('#PdpShippingPostal');
    const shippingResult = $('[data-pdp-shipping-result]');
    let shippingPostalCode = '', shippingRun = 0, shippingTimer;
    function shippingPolicyThreshold(threshold) {
      const known = Number.isFinite(threshold) && threshold > 0;
      $$('[data-pdp-policy-threshold]').forEach(el => { el.textContent = known ? money(threshold) : ''; });
      $$('[data-pdp-policy-quoted]').forEach(el => { el.hidden = !known; });
      $$('[data-pdp-policy-unquoted]').forEach(el => { el.hidden = known; });
    }
    // Planilla PRODUCT 28: la línea desaparecía ~1 s en cada cambio de
    // presentación. Sólo un umbral real la cambia: sin dato (null, consulta en
    // curso, sin tarifa) se queda la que está. Si nunca hubo uno no se inventa:
    // arranca con el último que vio el carrito (misma clave) o espera la
    // primera respuesta.
    const benefitKey = 'pluspet:free-shipping-threshold:' + ($('[data-cart-drawer]')?.dataset.shop || '');
    function releaseShippingSpace() { $('[data-pdp-shipping-benefit]')?.removeAttribute('data-shipping-pending'); }
    function shippingBenefitThreshold(threshold) {
      if (!(Number.isFinite(threshold) && threshold > 0)) return;
      const benefit = $('[data-pdp-shipping-benefit]');
      const text = $('[data-pdp-shipping-benefit-text]');
      if (text) text.textContent = 'Envío gratis a partir de ' + money(threshold);
      if (benefit) { benefit.removeAttribute('data-shipping-pending'); benefit.hidden = false; benefit.style.removeProperty('display'); }
      try { localStorage.setItem(benefitKey, JSON.stringify(threshold)); } catch (_) { /* sin storage */ }
    }
    try { shippingBenefitThreshold(JSON.parse(localStorage.getItem(benefitKey))); } catch (_) { /* sin storage */ }
    function shippingMessage(text, loading = false) {
      if (!shippingResult) return;
      shippingResult.textContent = text;
      shippingResult.classList.toggle('ppt-delivery-loading', loading);
      shippingBox?.setAttribute('aria-busy', String(loading));
    }
    async function quoteProductShipping() {
      clearTimeout(shippingTimer);
      const run = ++shippingRun;
      shippingPolicyThreshold(null);
      // Sin el bloque de "Calculá el envío" igual se consulta, en modo vista
      // previa, porque de esa respuesta sale el umbral real de envío gratis que
      // se muestra arriba del botón de compra.
      if (!shippingBox && !$('[data-pdp-shipping-benefit]')) return;
      const preview = !shippingPostalCode;
      const variant = byId(idInput?.value);
      const quantity = Number(qtyInput?.value || 1);
      if (!variant?.available || !Number.isInteger(quantity) || quantity < 1) {
        releaseShippingSpace(); shippingMessage('Elegí una presentación disponible y una cantidad válida.'); return;
      }
      const drawer = $('[data-cart-drawer]');
      const proxy = drawer?.dataset.proxy;
      if (!proxy) { releaseShippingSpace(); shippingMessage('Consultá el envío en el carrito.'); return; }
      const query = new URLSearchParams({shop: drawer.dataset.shop, fulfillment_method: 'delivery',
        postal_code: shippingPostalCode, subtotal_cents: String(variant.price * quantity),
        weight_grams: String((variant.grams || 0) * quantity), item_count: String(quantity), include_threshold: '1'});
      query.append('shopify_variant_ids[]', String(variant.id));
      if (!preview) shippingMessage('Calculando envío…', true);
      try {
        const res = await fetch(proxy.replace(/\/$/, '') + '/shipping_rates?' + query);
        if (!res.ok) throw new Error('shipping ' + res.status);
        const data = await res.json();
        if (run !== shippingRun) return;
        releaseShippingSpace();
        if (preview) {
          shippingBenefitThreshold(data.free_shipping_threshold_cents);
          return;
        }
        const prices = (data.rates || []).map(rate => Number(rate.price_cents)).filter(price => Number.isFinite(price) && price >= 0);
        if (!prices.length) { shippingMessage('No hay una tarifa de envío para este producto y código postal.'); return; }
        const price = Math.min(...prices);
        shippingPolicyThreshold(data.free_shipping_threshold_cents);
        let text = price === 0 ? 'Envío gratis' : 'Envío: ' + money(price);
        if (data.free_shipping_threshold_cents > variant.price * quantity) text += '. Gratis desde ' + money(data.free_shipping_threshold_cents) + ' para esta zona y producto';
        shippingMessage(text + '. Estimación para ' + quantity + (quantity === 1 ? ' unidad.' : ' unidades.'));
        shippingBenefitThreshold(data.free_shipping_threshold_cents);
      } catch (_) {
        if (run === shippingRun) { releaseShippingSpace(); shippingMessage('No pudimos calcular el envío. Reintentá.'); }
      }
    }
    function refreshProductShipping() {
      if (!shippingPostalCode && !$('[data-pdp-shipping-benefit]')) return;
      shippingRun++;
      shippingPolicyThreshold(null);
      clearTimeout(shippingTimer);
      if (shippingPostalCode) shippingMessage('Calculando envío…', true);
      shippingTimer = setTimeout(quoteProductShipping, 200);
    }
    $('[data-pdp-shipping-calculate]')?.addEventListener('click', () => {
      const L = window.PlusPetDelivery;
      const postal = L?.normalizePostal(shippingPostal?.value || '');
      const state = L?.postalState(postal);
      if (!state?.valid) { shippingMessage(state?.message || 'Ingresá tu código postal.'); return; }
      shippingPostal.value = postal;
      shippingPostalCode = postal;
      quoteProductShipping();
    });
    shippingPostal?.addEventListener('input', () => {
      shippingRun++; shippingPostalCode = ''; clearTimeout(shippingTimer);
      shippingPolicyThreshold(null);
      shippingMessage('Ingresá el código postal y tocá Calcular.');
    });
    shippingPostal?.addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); $('[data-pdp-shipping-calculate]')?.click(); }
    });
    qtyInput?.addEventListener('input', refreshProductShipping);
    qtyInput?.addEventListener('change', refreshProductShipping);


    addBestWeightBadge(pills, data.variants, 'ppt-pdp__best');
    function formatMoneyLike(cents, sample) {
      // Reusa el formato del precio ya renderizado por Shopify (símbolo + separador).
      const n = Math.round(cents / 100);
      const s = n.toLocaleString('es-AR', { maximumFractionDigits: 0 });
      const prefix = (sample || '$').replace(/[\d.,\s]+$/, '') || '$';
      return prefix + s;
    }
    function updateProductTitle(variant) {
      const title = $('#PdpTitle');
      if (!title) return;
      let name = title.dataset.productTitle || '';
      const brand = title.dataset.brand || '';
      const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const weightPattern = value => value.trim().split(/[.,]/).map(escapeRegex).join('[.,]').replace(/\s+/g, '\\s*');
      // Remove a catalog weight already suffixed to the product name before
      // appending the selected presentation. Keep descriptive numbers intact.
      data.variants.forEach(v => {
        if (/^\d[\d.,+\s]*(?:kg|g)$/i.test(v.title || '')) {
          const pattern = weightPattern(v.title).replace(/(kg|g)$/i, '\\s*$1');
          name = name.replace(new RegExp('\\s+' + pattern + '$', 'i'), '').trim();
        }
      });
      if (brand && !new RegExp('(^|\\s)' + escapeRegex(brand) + (brand.endsWith('+') ? '' : '\\+?') + '(?=\\s|$)', 'i').test(name)) name = brand + ' ' + name;
      const label = String(variant.title || '').trim();
      if (label && label.toLowerCase() !== 'default title') name += ' ' + label;
      title.textContent = name.trim();
    }
    let fadeTimer = null;
    function apply(v, skipImage, animate) {
      if (!v) return;
      updateProductTitle(v);
      // v7: el bloque de precio parpadea (opacity+blur 150ms) al cambiar de variante.
      const box = $('#PdpPriceBox');
      if (box && animate) {
        box.classList.add('is-fading');
        clearTimeout(fadeTimer);
        fadeTimer = setTimeout(() => box.classList.remove('is-fading'), 150);
      }
      const priceEl = $('#PdpPrice');
      if (priceEl) priceEl.textContent = v.price_formatted;

      const oldEl = $('#PdpOld');
      const discEl = $('#PdpDisc');
      const discPct = $('#PdpDiscPct');
      if (v.compare_at_price > v.price) {
        if (oldEl) { oldEl.textContent = v.compare_at_formatted; oldEl.style.display = ''; }
        if (discEl) {
          const pct = v.discount || Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
          if (discPct) discPct.textContent = pct; else discEl.textContent = '-' + pct + '%';
          discEl.style.display = '';
        }
      } else {
        if (oldEl) oldEl.style.display = 'none';
        if (discEl) discEl.style.display = 'none';
      }
      const perKgEl = $('#PdpPerKg');
      if (perKgEl) {
        const pk = pricePerKg(v.price, v.title, v.grams);
        const amt = $('#PdpPerKgAmt');
        if (pk && amt) { amt.textContent = formatMoneyLike(pk, v.price_formatted); perKgEl.hidden = false; }
        else perKgEl.hidden = true;
      }
      const netEl = $('#PdpNet');
      if (netEl && v.net_formatted) netEl.textContent = v.net_formatted;

      // Kilos de regalo: viajan en el nombre de la presentación ("13+2kg") y
      // se muestran en la línea de stock (Lovable (4)).
      const giftMatch = String(v.title || '').match(/\+\s*(\d+(?:[.,]\d+)?)\s*kg/i);
      const stockBox = $('#PdpStock');
      if (stockBox) stockBox.dataset.giftKg = giftMatch ? giftMatch[1] : '';

      // Imagen de la variante (data real): cambia la principal + activa su miniatura.
      if (v.image && !skipImage) {
        const mainImg = $('#PdpMainImg');
        if (mainImg) {
          updateProductImage(mainImg, v.image, v.image_width);
          if (v.image_alt) mainImg.alt = v.image_alt;
        }
        // Activa la miniatura de la variante; si no matchea ninguna, no borra la activa.
        var thumbs = $$('[data-pdp-thumb]');
        var match = thumbs.some((t) => String(t.dataset.mediaId) === String(v.media_id));
        if (match) {
          thumbs.forEach((t) => t.setAttribute('aria-current', String(String(t.dataset.mediaId) === String(v.media_id))));
        }
      }

      // El reintegro sigue al peso elegido: el de un 15kg no es el de un 2kg.
      // Con monto 0 se esconde entero — un "Sumás $0 de reintegro" se lee como
      // un error, no como "este producto no tiene".
      const cbEl = $('#PdpCashback');
      if (cbEl) {
        const cbAmt = $('#PdpCashbackAmt');
        if (v.cashback > 0) {
          if (cbAmt) cbAmt.textContent = v.cashback_formatted;
          cbEl.hidden = false;
        } else {
          cbEl.hidden = true;
        }
      }

      const stockEl = $('#PdpStock');
      if (stockEl) {
        const city = stockEl.dataset.city || 'Mendoza';
        const q = v.inventory_quantity;
        let txt = '';
        let out = false;
        let level = '';
        // Mismos umbrales que el Liquid de main-product (copy unificado del
        // diseño v8; con 10 o más no se muestra el número exacto — regla del
        // cliente). El fragmento fuerte va en <strong> coloreado por nivel.
        if (!v.available) { txt = 'Sin stock en ' + city; out = true; level = 'out'; }
        else if (q >= 10) { txt = '<strong>+10 unidades</strong> en ' + city; level = 'ok'; }
        else if (q === 1) { txt = '<strong>Última unidad</strong> en ' + city; level = 'critical'; }
        else if (q > 0) { txt = '<strong>' + q + ' unidades</strong> en ' + city; level = 'low'; }
        txt = withStockGift(txt, stockEl);
        const txtEl = $('#PdpStockTxt');
        if (txtEl) txtEl.innerHTML = txt; else stockEl.innerHTML = txt;
        stockEl.classList.toggle('ppt-pdp__stock--out', out);
        stockEl.setAttribute('data-stock-level', level);
        stockEl.style.display = txt ? '' : 'none';
      }
      // Sin stock: se esconden cantidad y envío, y el CTA pasa a "Agotado" (outline).
      const qtyWrap = $('#PdpQtyWrap');
      if (qtyWrap) qtyWrap.hidden = !v.available;
      const shipLine = $('#PdpShipLine');
      if (shipLine) shipLine.hidden = !v.available;

      const btn = $('#PdpAddBtn');
      if (btn) {
        btn.disabled = !v.available;
        const txtEl = $('#PdpAddTxt');
        if (txtEl) txtEl.textContent = v.available ? 'Agregar' : 'Agotado';
        else btn.textContent = v.available ? 'Agregar' : 'Agotado';
        btn.classList.toggle('btn--primary', !!v.available);
        btn.classList.toggle('btn--outline', !v.available);
      }

      // Cantidad: tope = stock real cuando el inventario está trackeado.
      if (qtyInput) {
        if (v.inventory_quantity != null && v.inventory_quantity > 0) {
          qtyInput.max = v.inventory_quantity;
          if (parseInt(qtyInput.value, 10) > v.inventory_quantity) qtyInput.value = v.inventory_quantity;
        } else {
          qtyInput.removeAttribute('max');
        }
        qtyInput.dispatchEvent(new Event('ppt:max'));
      }
      syncProductStock(idInput?.form);
    }

    // Pills de variante: cambian el id que se agrega + actualizan precio/stock.
    // Selecciona una variante (por id): marca el pill, actualiza el input y aplica.
    // skipImage=true cuando el cambio vino de clickear una miniatura (ya se ve la imagen correcta).
    function selectVariant(id, skipImage, animate = true) {
      pills.forEach((p) => {
        const active = String(p.dataset.id) === String(id);
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (idInput) idInput.value = id;
      apply(byId(id), skipImage, animate);
      refreshProductShipping();
    }

    pills.forEach((pill) => {
      pill.addEventListener('click', () => { if (!pill.disabled) selectVariant(pill.dataset.id, false); });
    });

    // Sync inverso: clickear una miniatura ligada a una variante selecciona esa variante
    // (sin re-swapear la imagen, que ya la muestra initPdpGallery).
    $$('[data-pdp-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const ids = (thumb.dataset.variantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!ids.length) return;
        const match = ids.find((id) => byId(id));
        if (match) selectVariant(match, true);
      });
    });

    // Selector de cantidad (+ / −), respeta min/max.
    if (qtyInput) {
      const clamp = () => {
        let n = parseInt(qtyInput.value, 10);
        if (isNaN(n) || n < 1) n = 1;
        const max = parseInt(qtyInput.max, 10);
        if (!isNaN(max) && n > max) n = max;
        qtyInput.value = n;
      };
      const dec = $('#PdpQtyWrap [data-qty-dec]') || $('[data-qty-dec]');
      const inc = $('#PdpQtyWrap [data-qty-inc]') || $('[data-qty-inc]');
      const syncBtns = () => {
        const n = parseInt(qtyInput.value, 10) || 1;
        const max = parseInt(qtyInput.max, 10);
        if (dec) dec.disabled = n <= 1;
        if (inc) inc.disabled = !isNaN(max) && n >= max;
      };
      if (dec) dec.addEventListener('click', () => { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) - 1; clamp(); syncBtns(); refreshProductShipping(); });
      if (inc) inc.addEventListener('click', () => { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; clamp(); syncBtns(); refreshProductShipping(); });
      qtyInput.addEventListener('change', () => { clamp(); syncBtns(); refreshProductShipping(); });
      qtyInput.addEventListener('ppt:max', syncBtns);
      setTimeout(syncBtns, 0);
    }

    const explicitVariant = byId(new URLSearchParams(window.location.search).get('variant'));
    const initialVariant = explicitVariant || bestWeightVariant(data.variants)[0] || byId(idInput?.value) || data.variants[0];
    if (initialVariant) selectVariant(initialVariant.id, false, false);
    const savedPostal = cartState?.attributes?._pluspet_method === 'delivery'
      ? window.PlusPetDelivery?.normalizePostal(cartState.attributes._pluspet_address_postal_code || '') : '';
    if (window.PlusPetDelivery?.postalState(savedPostal).valid) {
      shippingPostalCode = savedPostal;
      if (shippingPostal) shippingPostal.value = savedPostal;
      clearTimeout(shippingTimer);
      quoteProductShipping();
    }
  })();

  /* ============================================================
     Ficha de producto: galería accesible por teclado
     ============================================================ */
  (function initPdpPolicies() {
    $$('.ppt-pdp__policy').forEach(details => {
      const summary = $('summary', details);
      if (!summary) return;
      let animation = null, desiredOpen = details.open;
      summary.addEventListener('click', event => {
        if (!details.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
        event.preventDefault();
        const startHeight = details.getBoundingClientRect().height;
        desiredOpen = animation ? !desiredOpen : !details.open;
        if (animation) { animation.onfinish = null; animation.cancel(); }
        details.open = true;
        const endHeight = desiredOpen ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        details.style.overflow = 'hidden';
        animation = details.animate([{height:startHeight + 'px'}, {height:endHeight + 'px'}], {duration:200, easing:'ease-out'});
        const current = animation;
        current.onfinish = () => {
          if (animation !== current) return;
          details.open = desiredOpen;
          details.style.removeProperty('overflow');
          animation = null;
        };
      });
    });
  })();

  (function initPdpGallery() {
    const mainImg = $('#PdpMainImg');
    if (!mainImg) return;
    $$('[data-pdp-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        mainImg.src = btn.dataset.full;
        const label = btn.getAttribute('aria-label');
        if (label) mainImg.alt = label;
        $$('[data-pdp-thumb]').forEach((b) => b.removeAttribute('aria-current'));
        btn.setAttribute('aria-current', 'true');
      });
    });
  })();

  /* ============================================================
     Selector de ciudad: guarda la elección (cookie .pluspet.com.ar +
     localStorage) y redirige al mercado de esa ciudad (data-city-url).
     Un simple redirect entre subdominios (mendoza/ba/cordoba/rosario).
     ============================================================ */
  (function initCityDropdowns() {
    const wraps = $$('.ppt-citywrap');
    function setOpen(wrap, open) {
      wrap.classList.toggle('is-open', open);
      wrap.querySelector('[aria-haspopup]')?.setAttribute('aria-expanded', String(open));
    }
    wraps.forEach((wrap) => {
      const trigger = wrap.querySelector('[aria-haspopup]');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const open = !wrap.classList.contains('is-open');
        wraps.forEach((other) => setOpen(other, other === wrap && open));
      });
      wrap.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && wrap.classList.contains('is-open')) {
          event.preventDefault();
          setOpen(wrap, false);
          trigger.focus();
        }
      });
      wrap.addEventListener('focusout', (event) => {
        if (!wrap.contains(event.relatedTarget)) setOpen(wrap, false);
      });
    });
    document.addEventListener('click', (event) => {
      wraps.forEach((wrap) => { if (!wrap.contains(event.target)) setOpen(wrap, false); });
    });
  })();

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-city-select]');
    if (!btn) return;
    e.preventDefault();
    const label = btn.getAttribute('data-city-label') || '';
    const url = btn.getAttribute('data-city-url') || '';
    try {
      localStorage.setItem('pp_city', label);
      document.cookie = 'pp_city=' + encodeURIComponent(label) +
        '; path=/; domain=.pluspet.com.ar; max-age=31536000; samesite=lax';
    } catch (err) { /* almacenamiento no disponible */ }
    if (url) { window.location.assign(url); }
  });

  /* ============================================================
     Buscador con placeholder animado (efecto máquina de escribir),
     igual a lovable useTypewriter. Frases configurables (data-typewriter,
     una por línea), se pausa al enfocar y respeta prefers-reduced-motion.
     ============================================================ */
  (function () {
    const input = $('.ppt-search input[data-typewriter]');
    if (!input) return;
    const phrases = (input.dataset.typewriter || '')
      .split('\n').map((s) => s.trim()).filter(Boolean);
    if (phrases.length === 0) return;
    // v9: al pausarse, el placeholder vuelve al TEXTO FIJO del setting, no a la
    // última frase de la animación (antes coincidían por casualidad; si el
    // merchant cambia las frases, dejaban de coincidir).
    const idle = input.getAttribute('placeholder') || '';
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { input.setAttribute('placeholder', idle); return; }

    const TYPE = 60, ERASE = 35, HOLD = 2000, GAP = 400;
    let pi = 0, ci = 0, phase = 'typing', paused = false, timer = null, loaded = document.readyState === 'complete';
    let idleId = null;
    const set = (t) => input.setAttribute('placeholder', t);
    function cancel() {
      clearTimeout(timer);
      if (idleId !== null && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      idleId = null;
    }
    function tick() {
      if (paused || !loaded || document.visibilityState === 'hidden' || input.value) return;
      const cur = phrases[pi];
      if (phase === 'typing') {
        if (ci < cur.length) { ci++; set(cur.slice(0, ci)); timer = setTimeout(tick, TYPE); }
        else { phase = 'erasing'; timer = setTimeout(tick, HOLD); }
      } else {
        if (ci > 0) { ci--; set(cur.slice(0, ci)); timer = setTimeout(tick, ERASE); }
        else { pi = (pi + 1) % phrases.length; phase = 'typing'; timer = setTimeout(tick, GAP); }
      }
    }
    input.addEventListener('focus', () => { paused = true; cancel(); set(idle); });
    input.addEventListener('blur', () => {
      if (input.value) return; // no reanimar si el usuario ya escribió
      cancel();
      paused = false; pi = 0; ci = 0; phase = 'typing'; tick();
    });
    function start() {
      loaded = true;
      if (document.activeElement === input) return;
      if (window.requestIdleCallback) idleId = window.requestIdleCallback(() => { idleId = null; tick(); }, { timeout: 1000 });
      else timer = setTimeout(tick, 0);
    }
    if (loaded) start();
    else window.addEventListener('load', start, { once: true });
    document.addEventListener('visibilitychange', () => {
      cancel();
      if (document.visibilityState === 'visible') tick();
    });
  })();

  /* ============================================================
     Lovable (7), 13/10: foto de la ficha a pantalla completa
     (ProductImageLightbox.tsx). Rueda anclada bajo el cursor, pellizco, doble
     toque y botones; tope 4×; arrastre con zoom. Al cerrar vuelve a 1×.
     ============================================================ */
  (function () {
    const dialog = $('[data-pdp-lightbox]');
    const trigger = $('[data-pdp-zoom]');
    if (!dialog || !trigger) return;
    const frame = dialog.querySelector('[data-lightbox-frame]');
    const stage = dialog.querySelector('[data-lightbox-stage]');
    const img = dialog.querySelector('[data-lightbox-img]');
    const btnIn = dialog.querySelector('[data-lightbox-in]');
    const btnOut = dialog.querySelector('[data-lightbox-out]');
    const btnReset = dialog.querySelector('[data-lightbox-reset]');
    const hint = dialog.querySelector('[data-lightbox-hint]');
    const MIN = 1, MAX = 4;
    let zoom = 1, ox = 0, oy = 0;
    const pointers = new Map();
    let gesture = null;
    let lastTap = null;

    function render() {
      stage.style.transform = `translate(${ox}px, ${oy}px) scale(${zoom})`;
      frame.classList.toggle('is-zoomed', zoom > 1);
      btnOut.disabled = zoom <= MIN + 0.001;
      btnReset.disabled = zoom <= MIN + 0.001;
      btnIn.disabled = zoom >= MAX - 0.001;
      hint.hidden = zoom > 1.05;
    }
    // La caja de la foto mide lo que el visor: escalada a z, el recorrido
    // válido queda entre width·(1−z) y 0 (y lo mismo en alto).
    function clamp(x, y, z) {
      const r = frame.getBoundingClientRect();
      return [Math.min(0, Math.max(r.width * (1 - z), x)), Math.min(0, Math.max(r.height * (1 - z), y))];
    }
    function zoomAt(factor, px, py) {
      const next = Math.min(MAX, Math.max(MIN, zoom * factor));
      if (next === zoom) return;
      const k = next / zoom;
      [ox, oy] = clamp(px - (px - ox) * k, py - (py - oy) * k, next);
      zoom = next;
      render();
    }
    function zoomAtCenter(factor) {
      const r = frame.getBoundingClientRect();
      zoomAt(factor, r.width / 2, r.height / 2);
    }
    function reset() { zoom = 1; ox = 0; oy = 0; render(); }
    function local(e) {
      const r = frame.getBoundingClientRect();
      return [e.clientX - r.left, e.clientY - r.top];
    }
    // 26/10: tocar fuera de la foto cierra, no sólo la X. La foto va con
    // object-fit: contain, así que el <img> ocupa todo el visor: lo que se
    // pinta es el rectángulo con su proporción, centrado y escalado al zoom.
    function outsidePhoto(e) {
      const box = img.getBoundingClientRect();
      const w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h || !box.width || !box.height) return false;
      const k = Math.min(box.width / w, box.height / h);
      const pw = w * k, ph = h * k;
      const left = box.left + (box.width - pw) / 2, top = box.top + (box.height - ph) / 2;
      return e.clientX < left || e.clientX > left + pw || e.clientY < top || e.clientY > top + ph;
    }
    let closeOnClick = false;

    trigger.addEventListener('click', () => {
      const main = $('#PdpMainImg');
      if (!main) return;
      img.src = main.dataset.zoomSrc || main.currentSrc || main.src;
      img.alt = main.alt || '';
      reset();
      document.documentElement.style.overflow = 'hidden';
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    });
    function close() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
    dialog.addEventListener('close', () => {
      closeOnClick = false;
      document.documentElement.style.overflow = '';
      pointers.clear(); gesture = null; reset();
    });
    dialog.querySelector('[data-lightbox-close]').addEventListener('click', close);
    btnIn.addEventListener('click', () => zoomAtCenter(1.4));
    btnOut.addEventListener('click', () => zoomAtCenter(1 / 1.4));
    btnReset.addEventListener('click', reset);

    // Rueda: listener no pasivo para que preventDefault() frene el scroll;
    // el factor sale de la magnitud del delta, no de un paso fijo.
    frame.addEventListener('wheel', (e) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      const dy = e.deltaY * unit, dx = e.deltaX * unit;
      if (Math.abs(dy) >= Math.abs(dx)) {
        const [px, py] = local(e);
        zoomAt(Math.exp(-dy * 0.0015), px, py);
      } else {
        [ox, oy] = clamp(ox - dx, oy, zoom);
        render();
      }
    }, { passive: false });

    function beginPan(id) {
      const p = pointers.get(id);
      if (p) gesture = { mode: 'pan', x: p.x, y: p.y, ox, oy };
    }
    frame.addEventListener('pointerdown', (e) => {
      if (frame.setPointerCapture) { try { frame.setPointerCapture(e.pointerId); } catch (_) {} }
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) beginPan(e.pointerId);
      else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        gesture = { mode: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom, ox, oy };
      }
    });
    frame.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (!gesture) return;
      if (gesture.mode === 'pinch' && pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const next = Math.min(MAX, Math.max(MIN, gesture.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / gesture.dist)));
        const k = next / gesture.zoom;
        const r = frame.getBoundingClientRect();
        const mx = (a.x + b.x) / 2 - r.left, my = (a.y + b.y) / 2 - r.top;
        [ox, oy] = clamp(mx - (mx - gesture.ox) * k, my - (my - gesture.oy) * k, next);
        zoom = next;
        render();
      } else if (gesture.mode === 'pan' && zoom > 1) {
        [ox, oy] = clamp(gesture.ox + (e.clientX - gesture.x), gesture.oy + (e.clientY - gesture.y), zoom);
        render();
      }
    });
    function endPointer(e) {
      const start = gesture && gesture.mode === 'pan' ? gesture : null;
      pointers.delete(e.pointerId);
      // Doble toque: dos toques cortos en el mismo lugar acercan 2,5× ahí (o
      // vuelven a la foto completa). En touch, dblclick no siempre llega.
      if (start && pointers.size === 0 && Math.hypot(e.clientX - start.x, e.clientY - start.y) < 10) {
        // Un toque (sin arrastrar) fuera de la foto cierra. Se cierra en el
        // click, no acá: si el visor desapareciera en el pointerup, el click
        // caería en la ficha que quedó abajo.
        if (outsidePhoto(e)) { closeOnClick = true; lastTap = null; gesture = null; return; }
        const now = Date.now();
        if (lastTap && now - lastTap.t < 300 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
          lastTap = null;
          if (zoom > 1.05) reset();
          else { const [px, py] = local(e); zoomAt(2.5, px, py); }
        } else lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
      if (pointers.size === 0) gesture = null;
      else if (pointers.size === 1 && gesture && gesture.mode === 'pinch') beginPan([...pointers.keys()][0]);
    }
    frame.addEventListener('pointerup', endPointer);
    frame.addEventListener('click', () => {
      if (!closeOnClick) return;
      closeOnClick = false;
      close();
    });
    frame.addEventListener('pointercancel', endPointer);
    render();
  })();
})();
